import type React from 'react'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  Wrench, ShieldCheck, FileSearch, Server, RotateCcw, Terminal,
  Copy, Check, RefreshCw, AlertCircle, Loader2, ChevronDown,
  ThumbsUp, ThumbsDown, Download, Maximize2, Minimize2, RotateCw, X,
  FileText, Hash, Eye, EyeOff, Plus, Paperclip, Share2, FileDown, CloudUpload, ArrowLeft,
  ExternalLink, Image, Brain, Zap, Sparkles, Crown, ArrowUp,
  Layers, Camera, Mic, Square, Pencil, Code2, MoreHorizontal, Archive,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn, formatGbp } from '@/lib/utils'
import { useAppStore } from '@/store'
import { api } from '@/lib/api'
import { getToken } from '@/lib/auth'
import { useModelStatus } from '@/hooks/useApi'
import type { ChatMessage, ApprovalCard, MessageAttachment } from '@/types'
import { ArtifactCanvas, detectArtifact } from './ArtifactCanvas'
import type { ArtifactContent } from './ArtifactCanvas'
import { extractDocumentText } from './documentParsers'
import { MediaGrid, mediaItemsFromAttachments, mediaItemsFromToolCalls, mergeMediaItems, parseToolResultObject } from './ChatMedia'

const QUICK_ACTIONS = [
  { label: 'Health Check', icon: Server, prompt: 'Run a health check on all workers and report their status.' },
  { label: 'Run Audit', icon: FileSearch, prompt: 'Generate a full security audit report for ops-bridge.' },
  { label: 'Check Drift', icon: RotateCcw, prompt: 'Check for configuration drift across all workers.' },
  { label: 'Security Scan', icon: ShieldCheck, prompt: 'Run a full security scan and report any new findings.' },
]

const IDEA_ACTIONS = [
  { label: 'Load ideas.md', icon: FileText, prompt: 'Call get_pipeline_queue to list all pipeline ideas with their current status and stage (this avoids the 181KB file truncation issue). Show me every idea — number, title, status, and current stage.' },
  { label: 'Add new idea', icon: Plus, prompt: "I'd like to add a new idea. First call get_pipeline_queue so we know what ideas already exist and what number comes next. Then let's discuss the new concept — I'll describe it and you help me refine it. Once ready, write it to architecture/ideas.md (version bump included) and insert a new row into pipeline_items." },
  { label: 'Update idea', icon: Sparkles, prompt: "I'd like to update an existing idea. Call get_pipeline_queue to list all current ideas so I can pick one. Then we'll discuss the changes, and you'll write the updated content back to architecture/ideas.md." },
]

const MODEL_GROUPS = [
  {
    group: 'OPS',
    desc: 'Internal OpsBridge AI — grounded in platform knowledge',
    models: [
      { mode: 'ops-mini', label: 'OPS Mini', provider: 'OPS', desc: 'Fast internal Workers AI role' },
      { mode: 'ops-1', label: 'OPS 1', provider: 'OPS', desc: 'Native Intelligence — tool orchestration + deep reasoning' },
    ],
  },
  {
    group: 'Fast',
    desc: 'Quick responses, low cost',
    models: [
      { mode: 'haiku', label: 'Haiku 4.5', provider: 'Anthropic', desc: 'Fast Claude' },
      { mode: 'gpt-mini', label: 'GPT-4.1 Mini', provider: 'OpenAI', desc: 'Fast OpenAI' },
      { mode: 'flash', label: 'Gemini Flash', provider: 'Gemini', desc: 'Fast Gemini' },
    ],
  },
  {
    group: 'Balanced',
    desc: 'Quality + speed balance',
    models: [
      { mode: 'enterprise', label: 'Sonnet 5', provider: 'Anthropic', desc: 'Coding & ops' },
      { mode: 'professional', label: 'GPT-4.1', provider: 'OpenAI', desc: 'Balanced reasoning' },
      { mode: 'research', label: 'Gemini 3.1 Pro', provider: 'Gemini', desc: 'Research & analysis' },
    ],
  },
  {
    group: 'Advanced',
    desc: 'Maximum capability',
    models: [
      { mode: 'opus-4-6', label: 'Opus 4.6', provider: 'Anthropic', desc: 'Deep analysis' },
      { mode: 'opus-4-7', label: 'Opus 4.7', provider: 'Anthropic', desc: 'Advanced reasoning' },
      { mode: 'opus-4-8', label: 'Opus 4.8', provider: 'Anthropic', desc: 'Frontier intelligence' },
      { mode: 'gpt-5', label: 'GPT-5.5', provider: 'OpenAI', desc: 'OpenAI frontier' },
      { mode: 'o3', label: 'o3', provider: 'OpenAI', desc: 'Reasoning specialist' },
    ],
  },
  {
    group: 'Combined',
    desc: 'Multi-provider committee',
    models: [
      { mode: 'combined', label: 'Committee', provider: 'All', desc: 'Gemini + OpenAI + Claude' },
    ],
  },
]

function compactModelName(modelId?: string | null) {
  if (!modelId) return null
  const tail = modelId.split('/').pop() || modelId
  return tail
}

function roleLabel(role: string) {
  if (role === 'tool_orchestrator') return 'Tool Orchestrator'
  if (role === 'deep_reasoner') return 'Deep Reasoner'
  if (role === 'fast_general') return 'Fast General'
  if (role === 'vision') return 'Vision'
  if (role === 'verifier') return 'Verifier'
  return role.replaceAll('_', ' ')
}

const DEV_ACTIONS = [
  { label: 'Gateway security review', prompt: 'Review the admin gateway security settings and explain proposed changes. Do not modify gateway implementation or security settings without my explicit approval for those changes.' },
  { label: 'Worker Map', prompt: 'Show the complete worker map with all endpoints and bindings.' },
  { label: 'KV Status', prompt: 'List all KV namespaces with key counts and usage.' },
  { label: 'D1 Schema', prompt: 'Show the D1 database schema for kora-control-db.' },
  { label: 'Tool Tiers', prompt: 'List all tools grouped by their approval tier.' },
  { label: 'Recent Deploys', prompt: 'Show the last 10 deployments across all workers.' },
]

const FEEDBACK_REASONS = [
  { value: 'incorrect', label: 'Incorrect' },
  { value: 'incomplete', label: 'Incomplete' },
  { value: 'formatting', label: 'Formatting' },
  { value: 'tool_failure', label: 'Tool Failure' },
  { value: 'other', label: 'Other' },
]

const POSITIVE_REASONS = [
  { value: 'helpful', label: 'Helpful' },
  { value: 'accurate', label: 'Accurate' },
  { value: 'clear', label: 'Clear explanation' },
  { value: 'solved', label: 'Solved my problem' },
]

// --- Utilities ---

function useCopy() {
  const [copied, setCopied] = useState(false)
  const copy = useCallback((text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [])
  return { copied, copy }
}

function downloadText(content: string, filename: string) {
  const a = document.createElement('a')
  a.href = 'data:text/plain;charset=utf-8,' + encodeURIComponent(content)
  a.download = filename
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

interface FileAttachment {
  id: string
  name: string
  size: number
  type: string
  kind: 'image' | 'document' | 'video'
  data: string
  rawData?: string
  isImage: boolean
  processing?: boolean
  error?: string
}

const MAX_FILE_SIZE = 10 * 1024 * 1024
const MAX_TOTAL_ATTACHMENT_BYTES = 20 * 1024 * 1024
const MAX_FILES = 10
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
const VIDEO_TYPES = ['video/mp4', 'video/webm']

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error || new Error('Unable to read file'))
    reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '')
    reader.readAsDataURL(file)
  })
}



function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + 'B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB'
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB'
}

function extractSections(content: string): { id: string; title: string; level: number }[] {
  const lines = content.split('\n')
  const sections: { id: string; title: string; level: number }[] = []
  for (const line of lines) {
    const match = line.match(/^(#{1,4})\s+(.+)/)
    if (match) {
      const title = match[2].replace(/[*_`]/g, '')
      sections.push({ id: title.toLowerCase().replace(/[^a-z0-9]+/g, '-'), title, level: match[1].length })
    }
  }
  return sections
}

function isDocumentResponse(content: string): boolean {
  if (!content) return false
  const headingCount = (content.match(/^#{1,4}\s/gm) || []).length
  const tableCount = (content.match(/\|.*\|/g) || []).length
  const lineCount = content.split('\n').length
  return headingCount >= 3 || tableCount >= 4 || lineCount > 40
}

// --- Code block with copy ---

function CodeBlock({ code, language }: { code: string; language: string }) {
  const { copied, copy } = useCopy()
  return (
    <div className="relative my-3 rounded-lg overflow-hidden border border-white/5">
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#1a1a2e] border-b border-white/5">
        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-mono">{language}</span>
        <button onClick={() => copy(code)} className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-white transition-colors">
          {copied ? <><Check className="h-3 w-3 text-green-400" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
        </button>
      </div>
      <SyntaxHighlighter style={oneDark} language={language} PreTag="div" customStyle={{ margin: 0, borderRadius: 0, fontSize: '12px', whiteSpace: 'pre-wrap', overflowX: 'auto', maxWidth: '100%' }}>
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

// --- Markdown image renderer ---
// This is the actual path images take -- ReactMarkdown's default <img>
// handling for standard markdown image syntax in the model's own text
// response. A separate, more elaborate rendering path existed elsewhere in
// this file (reveal animation, click-to-preview, download button) but was
// never actually reachable -- nothing ever constructed the data shape it
// checked for. Porting that same intent here, onto the path that's
// genuinely exercised, rather than just deleting it and losing the
// behaviour that was actually asked for.
function MarkdownImage({ src, alt }: { src?: string; alt?: string }) {
  const [loaded, setLoaded] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [mediaStatus, setMediaStatus] = useState<string | null>(null)
  if (!src) return null

  const shareImage = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: alt || 'OPS image', url: src.startsWith('data:') ? undefined : src })
      } else if (!src.startsWith('data:')) {
        await navigator.clipboard.writeText(src)
        setMediaStatus('Image link copied')
      }
    } catch (e) {
      if (e instanceof Error && e.name !== 'AbortError') setMediaStatus('Share failed')
    }
  }

  const saveImage = async () => {
    try {
      const response = await fetch(src)
      if (!response.ok) throw new Error('Image fetch failed')
      const blob = await response.blob()
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onerror = () => reject(reader.error)
        reader.onload = () => resolve(String(reader.result || '').split(',')[1] || '')
        reader.readAsDataURL(blob)
      })
      await api.libraryUpload(`${(alt || 'ops-image').replace(/[^a-zA-Z0-9._-]+/g, '_')}.png`, blob.type || 'image/png', base64)
      setMediaStatus('Saved to Library')
    } catch {
      setMediaStatus('Save failed')
    }
  }

  return (
    <>
      <img
        key={src}
        src={src}
        alt={alt || 'Generated image'}
        onLoad={() => setLoaded(true)}
        onClick={() => setExpanded(true)}
        className={cn(
          'max-w-full sm:max-w-[400px] rounded-lg hover:brightness-110 transition-[filter] cursor-pointer block my-2',
          loaded ? 'image-reveal' : 'opacity-0',
        )}
      />
      {expanded && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
          onClick={(e) => { if (e.target === e.currentTarget) setExpanded(false) }}
        >
          <img src={src} alt={alt || 'Generated image'} className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg" />
          <div className="absolute top-4 right-5 flex items-center gap-3">
            <a href={src} download className="text-white/70 hover:text-white transition-colors" onClick={(e) => e.stopPropagation()} title="Download">
              <Download className="h-5 w-5" />
            </a>
            <button onClick={() => { void shareImage() }} className="text-white/70 hover:text-white transition-colors" title="Share">
              <Share2 className="h-5 w-5" />
            </button>
            <button onClick={() => { void saveImage() }} className="text-white/70 hover:text-white transition-colors" title="Save to Library">
              <CloudUpload className="h-5 w-5" />
            </button>
            <button onClick={() => setExpanded(false)} className="text-white/70 hover:text-white transition-colors" title="Close">
              <X className="h-6 w-6" />
            </button>
          </div>
          {mediaStatus && <div className="absolute bottom-5 rounded-lg bg-black/70 px-3 py-2 text-xs text-white">{mediaStatus}</div>}
        </div>,
        document.body
      )}
    </>
  )
}

// --- Markdown renderer ---

function MarkdownContent({ content }: { content: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        code({ className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '')
          const codeStr = String(children).replace(/\n$/, '')
          if (match && match[1] === 'html') return <InlineHtmlPreview html={codeStr} />
          if (match) return <CodeBlock code={codeStr} language={match[1]} />
          return <code className="px-1.5 py-0.5 rounded bg-white/5 text-xs font-mono text-accent" {...props}>{children}</code>
        },
        pre({ children }) { return <>{children}</> },
        img({ src, alt }) { return <MarkdownImage src={typeof src === 'string' ? src : undefined} alt={alt} /> },
        h1({ children }) { return <h1 className="text-xl font-bold mt-6 mb-3 pb-2 border-b border-white/10 text-text-primary">{children}</h1> },
        h2({ children }) { return <h2 className="text-lg font-semibold mt-5 mb-2 text-text-primary">{children}</h2> },
        h3({ children }) { return <h3 className="text-base font-semibold mt-4 mb-2 text-text-primary">{children}</h3> },
        h4({ children }) { return <h4 className="text-sm font-semibold mt-3 mb-1 text-text-secondary">{children}</h4> },
        p({ children }) { return <p className="mb-3 last:mb-0 leading-relaxed">{children}</p> },
        ul({ children }) { return <ul className="list-disc pl-5 mb-3 space-y-1">{children}</ul> },
        ol({ children }) { return <ol className="list-decimal pl-5 mb-3 space-y-1">{children}</ol> },
        li({ children }) { return <li className="leading-relaxed">{children}</li> },
        table({ children }) {
          return <div className="overflow-x-auto my-3 rounded-lg border border-white/10"><table className="text-xs border-collapse w-full">{children}</table></div>
        },
        th({ children }) { return <th className="border-b border-white/10 px-3 py-2 bg-white/5 text-left font-semibold text-text-primary">{children}</th> },
        td({ children }) { return <td className="border-b border-white/5 px-3 py-2 text-text-secondary">{children}</td> },
        blockquote({ children }) { return <blockquote className="border-l-3 border-accent/50 pl-4 my-3 text-text-muted italic bg-accent/5 py-2 pr-3 rounded-r-lg">{children}</blockquote> },
        hr() { return <hr className="my-4 border-white/10" /> },
        strong({ children }) { return <strong className="font-semibold text-text-primary">{children}</strong> },
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

function HtmlContent({ html }: { html: string }) {
  return <iframe srcDoc={html} sandbox="allow-scripts" className="w-full min-h-[200px] rounded-lg border border-border my-2" title="Rendered content" />
}

// Direct complaint: an ```html fenced block in a chat message was showing
// as raw, dimmed source code (standard markdown code-block behaviour) --
// genuinely correct markdown handling, but not what someone actually wants
// to see for a document that's meant to be looked at. The canvas popup
// already renders this properly; this brings the same rendered view
// directly into the chat message itself, defaulting to Preview (matching
// the canvas's own default), with Code still one tap away rather than
// removed entirely.
function InlineHtmlPreview({ html }: { html: string }) {
  const [tab, setTab] = useState<'preview' | 'code'>('preview')
  return (
    <div className="my-2 rounded-lg border border-border overflow-hidden">
      <div className="flex items-center gap-1 px-2 pt-1.5 bg-surface-elevated border-b border-border">
        <button
          onClick={() => setTab('preview')}
          className={cn('px-2.5 py-1 text-[11px] font-medium rounded-t-md transition-colors', tab === 'preview' ? 'bg-surface text-accent' : 'text-text-muted hover:text-text-secondary')}
        >
          Preview
        </button>
        <button
          onClick={() => setTab('code')}
          className={cn('px-2.5 py-1 text-[11px] font-medium rounded-t-md transition-colors', tab === 'code' ? 'bg-surface text-accent' : 'text-text-muted hover:text-text-secondary')}
        >
          Code
        </button>
      </div>
      {tab === 'preview' ? <HtmlContent html={html} /> : <CodeBlock code={html} language="html" />}
    </div>
  )
}

// --- Tool calls display ---

const TOOL_PURPOSES: Record<string, string> = {
  generate_image: 'Generating the requested image',
  get_image: 'Retrieving the generated image',
  get_image_status: 'Checking image generation status',
  generate_video: 'Generating the requested video',
  get_video: 'Retrieving the generated video',
  get_video_status: 'Checking video generation status',
  search_platform: 'Searching verified platform sources',
  read_source: 'Reading the current source of truth',
  read_file: 'Reading the requested file',
  read_file_section: 'Reading the relevant file section',
  run_test: 'Running verification tests',
  check_typescript: 'Checking TypeScript',
  vps_deploy: 'Deploying the reviewed change',
  ops_pages_deploy: 'Deploying the reviewed dashboard change',
}

function normaliseToolResultString(result: unknown): string | undefined {
  if (result == null) return undefined
  if (typeof result !== 'string') {
    try { return JSON.stringify(result) } catch { return undefined }
  }
  let current: unknown = result
  for (let i = 0; i < 3; i += 1) {
    if (current && typeof current === 'object') {
      try { return JSON.stringify(current) } catch { return result }
    }
    if (typeof current !== 'string') break
    try { current = JSON.parse(current) } catch { return result }
  }
  return typeof current === 'string' ? current : result
}

function toolResultSummary(tool: NonNullable<ChatMessage['toolCalls']>[number]): string | null {
  const obj = parseToolResultObject(tool.result)
  if (!obj) return null
  if (tool.name.includes('image')) {
    const id = obj.image_id || obj.job_id
    const model = obj.model_used
    return [id ? String(id) : null, model ? String(model) : null].filter(Boolean).join(' · ') || null
  }
  if (tool.name.includes('video')) {
    const id = obj.video_id || obj.job_id
    return id ? String(id) : null
  }
  return null
}

function ToolCallsBar({ toolCalls, active }: { toolCalls: NonNullable<ChatMessage['toolCalls']>; active?: boolean }) {
  const [expanded, setExpanded] = useState(false)
  const running = toolCalls.filter(t => t.status === 'running').length
  const succeeded = toolCalls.filter(t => t.status === 'success').length
  const failed = toolCalls.filter(t => t.status === 'error').length
  const mediaItems = useMemo(() => mediaItemsFromToolCalls(toolCalls), [toolCalls])
  const generatedSucceeded = toolCalls.some(t => ['generate_image', 'get_image', 'generate_video', 'get_video'].includes(t.name) && t.status === 'success')
  const mediaMissing = generatedSucceeded && mediaItems.length === 0 && !active

  useEffect(() => {
    if (!active) setExpanded(false)
  }, [active])

  return (
    <div className="mb-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 py-1 text-[11px] text-text-muted hover:text-text-secondary transition-colors"
        aria-expanded={expanded}
      >
        <Wrench className="h-3.5 w-3.5" />
        <span className="font-medium">Activity</span>
        {running > 0 && <Badge variant="info" className="text-[9px]">{running} running</Badge>}
        {failed > 0 && <Badge variant="danger" className="text-[9px]">{failed} failed</Badge>}
        {running === 0 && failed === 0 && succeeded > 0 && (
          <span className="text-[10px] text-text-muted">{succeeded} tool{succeeded === 1 ? '' : 's'}</span>
        )}
        <ChevronDown className={cn('h-3 w-3 transition-transform', !expanded && '-rotate-90')} />
      </button>
      {expanded && (
        <div className="mt-1.5 ml-1 space-y-1.5 border-l border-border/50 pl-3">
          {toolCalls.map((tc) => {
            const purpose = TOOL_PURPOSES[tc.name] || `Using ${tc.name}`
            const resultSummary = toolResultSummary(tc)
            return (
              <div key={tc.id} className="flex items-start gap-2 text-[11px]">
                <span className={cn('mt-1 h-1.5 w-1.5 shrink-0 rounded-full', tc.status === 'success' ? 'bg-success' : tc.status === 'error' ? 'bg-danger' : tc.status === 'running' ? 'bg-warning animate-pulse' : 'bg-text-muted')} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-text-secondary">{purpose}</span>
                    <span className="font-mono text-[9px] text-text-muted">{tc.name}</span>
                    {tc.duration != null && <span className="text-[9px] text-text-muted">{tc.duration}ms</span>}
                  </div>
                  {resultSummary && <div className="mt-0.5 text-[9px] text-text-muted">{resultSummary}</div>}
                  {tc.status === 'error' && <div className="mt-0.5 text-[9px] text-danger">This step did not complete successfully.</div>}
                </div>
              </div>
            )
          })}
          {mediaMissing && (
            <div className="flex items-start gap-1.5 text-[9px] text-warning">
              <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>Generated media completed but no renderable media result was attached.</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// --- Safe reasoning summary ---
// OPS intentionally shows concise decision/execution summaries rather than raw
// hidden chain-of-thought. This gives the operator useful transparency without
// exposing provider-internal scratch reasoning.

function completedReasoningLines(
  reasoning?: NonNullable<ChatMessage['reasoning']>,
  toolCalls?: NonNullable<ChatMessage['toolCalls']>,
): string[] {
  const lines: string[] = []
  const successfulTools = (toolCalls || []).filter((tool) => tool.status === 'success')

  const generatedImages = successfulTools.filter((tool) => tool.name === 'generate_image').length
  const generatedVideos = successfulTools.filter((tool) => tool.name === 'generate_video').length

  if (generatedImages > 0) {
    lines.push(generatedImages === 1
      ? 'Generated the requested image and attached it to the conversation.'
      : `Generated ${generatedImages} requested images and attached them to the conversation.`)
  }
  if (generatedVideos > 0) {
    lines.push(generatedVideos === 1
      ? 'Generated the requested video and attached it to the conversation.'
      : `Generated ${generatedVideos} requested videos and attached them to the conversation.`)
  }

  const otherPurposes = Array.from(new Set(
    successfulTools
      .filter((tool) => !['generate_image', 'generate_video'].includes(tool.name))
      .map((tool) => TOOL_PURPOSES[tool.name] || `Used ${tool.name}`)
  ))
  if (otherPurposes.length > 0) {
    lines.push(`${otherPurposes.slice(0, 3).join('; ')}.`)
  }

  const reviewedContext = (reasoning || []).some((entry) => {
    const decision = entry.decision.toLowerCase()
    return !['model selection', 'tool selection'].includes(decision)
  })

  if (reviewedContext && lines.length < 3) {
    lines.push('Reviewed relevant OPS context before answering.')
  }

  if (lines.length === 0) {
    lines.push('Reviewed the request and answered directly from the available conversation context.')
  }

  return lines.slice(0, 3)
}

function ReasoningSummaryPanel({
  reasoning,
  toolCalls,
  active,
  provider,
  model,
  fallback,
  fallbackReason,
}: {
  reasoning?: NonNullable<ChatMessage['reasoning']>
  toolCalls?: NonNullable<ChatMessage['toolCalls']>
  active?: boolean
  provider?: string
  model?: string
  fallback?: boolean
  fallbackReason?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const entries = reasoning || []
  const activeEntries = useMemo(
    () => entries.filter((entry) => ['model selection', 'tool selection'].includes(entry.decision.toLowerCase())),
    [entries],
  )
  const reviewedContext = useMemo(
    () => entries.some((entry) => !['model selection', 'tool selection'].includes(entry.decision.toLowerCase())),
    [entries],
  )
  const completedLines = useMemo(() => completedReasoningLines(reasoning, toolCalls), [reasoning, toolCalls])

  useEffect(() => {
    if (!active) setExpanded(false)
  }, [active])

  if (!active && entries.length === 0 && (toolCalls?.length || 0) === 0 && !provider) return null

  return (
    <div className="mb-2">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 py-1 text-[12px] text-text-muted hover:text-text-secondary transition-colors"
        aria-expanded={expanded}
      >
        {active ? (
          <span className="ops-octagon-spin flex-shrink-0">
            <svg width="13" height="13" viewBox="0 0 32 32" fill="none">
              <defs>
                <linearGradient id="chatOctGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6d28d9" />
                  <stop offset="100%" stopColor="#9333ea" />
                </linearGradient>
              </defs>
              <polygon points="10,2 22,2 30,10 30,22 22,30 10,30 2,22 2,10" fill="url(#chatOctGrad)" stroke="#c084fc" strokeWidth="1.5" />
            </svg>
          </span>
        ) : (
          <Brain className="h-3.5 w-3.5" />
        )}
        <span className="font-medium">Thinking</span>
        <ChevronDown className={cn('h-3 w-3 transition-transform', !expanded && '-rotate-90')} />
      </button>

      {expanded && (
        <div className="mt-1.5 ml-1 space-y-1.5 border-l border-border/50 pl-3">
          {active ? (
            <>
              {activeEntries.length === 0 && !reviewedContext && (
                <div className="text-[11px] text-text-muted">Reviewing the request.</div>
              )}
              {activeEntries.map((entry, index) => (
                <div key={`${entry.decision}-${index}`} className="text-[11px] leading-relaxed">
                  <span className="font-medium text-text-secondary">{entry.decision.replace(/_/g, ' ')}:</span>
                  {entry.reason && <span className="ml-1 text-text-muted">{entry.reason}</span>}
                </div>
              ))}
              {reviewedContext && (
                <div className="text-[11px] leading-relaxed text-text-muted">Reviewing relevant OPS context and evidence.</div>
              )}
            </>
          ) : (
            <>
              {completedLines.map((line, index) => (
                <div key={index} className="text-[11px] leading-relaxed text-text-muted">{line}</div>
              ))}
              {provider && (
                <div className="pt-1 text-[9px] text-text-muted/75">
                  {provider}{model ? ` / ${model}` : ''}
                  {fallback ? ' · fallback' : ''}
                  {fallbackReason ? ` · ${fallbackReason}` : ''}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// --- Approval card ---

function InlineApprovalCard({ card, onApprove, onReject }: { card: ApprovalCard; onApprove: () => void; onReject: () => void }) {
  return (
    <div className="mt-4 p-4 rounded-lg border border-warning/30 bg-warning/5">
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="h-4 w-4 text-warning" />
        <span className="text-xs font-semibold">Tier {card.tier} Approval Required</span>
        <Badge variant={card.status === 'approved' ? 'success' : card.status === 'denied' ? 'danger' : 'warning'} className="text-[10px]">{card.status}</Badge>
      </div>
      <p className="text-xs opacity-80 mb-1 font-mono">{card.action}</p>
      <p className="text-xs opacity-70 mb-3">{card.details}</p>
      {card.status === 'pending' && (
        <div className="flex gap-2">
          <Button size="sm" className="h-7 text-xs" onClick={onApprove}>Approve</Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onReject}>Deny</Button>
        </div>
      )}
    </div>
  )
}

// --- Portal menu: renders children at document.body to escape overflow:hidden clips ---

function PortalMenu({ anchorRef, onClose, children, direction = 'up' }: {
  anchorRef: React.RefObject<HTMLDivElement | null>
  onClose: () => void
  children: React.ReactNode
  direction?: 'up' | 'right' | 'down'
}) {
  const [pos, setPos] = useState<React.CSSProperties | null>(null)

  useEffect(() => {
    if (anchorRef.current) {
      const r = anchorRef.current.getBoundingClientRect()
      if (direction === 'right') {
        setPos({ bottom: window.innerHeight - r.bottom, left: r.right + 8 })
      } else if (direction === 'down') {
        setPos({ top: r.bottom + 8, right: Math.max(12, window.innerWidth - r.right), maxWidth: 'calc(100vw - 24px)' })
      } else {
        setPos({ top: r.top, right: window.innerWidth - r.right, transform: 'translateY(-100%) translateY(-6px)' })
      }
    }
  }, [anchorRef, direction])

  useEffect(() => {
    const handleDown = (e: MouseEvent) => {
      if (!(e.target as Element)?.closest('[data-portal-menu]')) onClose()
    }
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', handleDown)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleDown)
      document.removeEventListener('keydown', handleKey)
    }
  }, [onClose])

  if (!pos) return null
  return createPortal(
    <div
      data-portal-menu
      style={{ position: 'fixed', ...pos, zIndex: 9999 }}
    >
      {children}
    </div>,
    document.body
  )
}

// --- Feedback modal ---

function FeedbackModal({ type, onSubmit, onClose }: {
  type: 'positive' | 'negative'
  onSubmit: (reason: string, comment?: string) => void
  onClose: () => void
}) {
  const [selected, setSelected] = useState('')
  const [comment, setComment] = useState('')
  const reasons = type === 'positive' ? POSITIVE_REASONS : FEEDBACK_REASONS

  const submit = (reason: string) => {
    setSelected(reason)
    onSubmit(reason, comment || undefined)
  }

  return (
    <div className="w-60 rounded-lg border border-white/10 bg-surface-elevated shadow-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-text-primary">
          {type === 'positive' ? 'What was great?' : 'What went wrong?'}
        </span>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X className="h-3 w-3" /></button>
      </div>
      <div className="space-y-0.5 mb-2">
        {reasons.map(r => (
          <button
            key={r.value}
            onClick={() => submit(r.value)}
            className={cn(
              'w-full text-left px-2 py-1.5 rounded text-xs transition-colors',
              selected === r.value ? 'bg-accent/20 text-accent' : 'text-text-secondary hover:bg-white/5'
            )}
          >
            {r.label}
          </button>
        ))}
      </div>
      <textarea
        placeholder="Optional comment…"
        value={comment}
        onChange={e => setComment(e.target.value)}
        className="w-full text-xs bg-white/5 border border-white/10 rounded px-2 py-1.5 text-text-primary placeholder:text-text-muted resize-none h-14 focus:outline-none focus:border-accent/40"
      />
      <button
        onClick={() => onSubmit(selected || 'other', comment || undefined)}
        className="mt-2 w-full py-1.5 rounded text-xs bg-accent text-white hover:bg-accent-hover transition-colors"
      >
        Submit
      </button>
    </div>
  )
}

// --- Section navigator for large docs ---

function SectionNav({ sections, onJump }: { sections: { id: string; title: string; level: number }[]; onJump: (id: string) => void }) {
  const [open, setOpen] = useState(false)
  if (sections.length < 3) return null
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1 text-text-muted hover:text-text-primary transition-colors" title="Sections">
        <Hash className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 z-20 w-64 max-h-60 overflow-y-auto rounded-lg border border-white/10 bg-surface-elevated shadow-xl p-2">
          <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider px-2 pb-1 mb-1 border-b border-white/5">Sections</div>
          {sections.map(s => (
            <button
              key={s.id}
              onClick={() => { onJump(s.id); setOpen(false) }}
              className="w-full text-left px-2 py-1 rounded text-xs text-text-secondary hover:bg-white/5 hover:text-text-primary transition-colors"
              style={{ paddingLeft: `${(s.level - 1) * 12 + 8}px` }}
            >
              {s.title}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Save to popover ---

function SaveToPopover({ content, onClose }: { content: string; onClose: () => void }) {
  const [saving, setSaving] = useState<string | null>(null)
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null)

  const handleLocal = () => {
    downloadText(content, `ops-bridge-export-${Date.now()}.md`)
    onClose()
  }

  const handleCloud = async (service: string, label: string) => {
    setSaving(service)
    setResult(null)
    try {
      const res = await api.chatSaveTo(service, content)
      if (res.success) {
        setResult({ ok: true, msg: res.message || `Saved to ${label}` })
        if (res.url) window.open(res.url, '_blank')
        setTimeout(onClose, 1500)
      } else {
        setResult({ ok: false, msg: res.message || `Failed to save to ${label}` })
      }
    } catch {
      setResult({ ok: false, msg: `Error saving to ${label}` })
    } finally {
      setSaving(null)
    }
  }

  const cloudServices = [
    { id: 'sharepoint', label: 'SharePoint', sub: 'Microsoft 365', iconBg: 'bg-blue-500/10', iconColor: 'text-blue-400', Icon: ExternalLink },
    { id: 'github-gist', label: 'GitHub Gist', sub: 'Secret gist', iconBg: 'bg-neutral-500/10', iconColor: 'text-neutral-400', Icon: FileDown },
  ]

  const comingSoon = ['Notion', 'OneDrive', 'Google Drive']

  return (
    <div className="w-64 rounded-xl border border-border bg-surface shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <CloudUpload className="h-3.5 w-3.5 text-accent" />
          <span className="text-sm font-semibold text-text-primary">Save to</span>
        </div>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Feedback */}
      {result && (
        <div className={`mx-3 mt-3 px-3 py-2 rounded-lg text-xs font-medium ${result.ok ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}>
          {result.msg}
        </div>
      )}

      <div className="p-3 space-y-1">
        {/* Local */}
        <p className="px-1 pb-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider">Local</p>
        <button
          onClick={handleLocal}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-surface-elevated transition-colors group"
        >
          <div className="h-8 w-8 rounded-lg bg-surface-elevated group-hover:bg-accent/10 flex items-center justify-center shrink-0 transition-colors">
            <FileText className="h-3.5 w-3.5 text-text-muted group-hover:text-accent transition-colors" />
          </div>
          <div className="text-left">
            <p className="text-xs font-medium text-text-primary">Markdown file</p>
            <p className="text-[11px] text-text-muted">Download to device</p>
          </div>
        </button>

        <div className="border-t border-border my-2" />

        {/* Cloud */}
        <p className="px-1 pb-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider">Cloud storage</p>
        {cloudServices.map(({ id, label, sub, iconBg, iconColor, Icon }) => (
          <button
            key={id}
            onClick={() => handleCloud(id, label)}
            disabled={!!saving}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-surface-elevated transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div className={`h-8 w-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
              {saving === id
                ? <Loader2 className={`h-3.5 w-3.5 ${iconColor} animate-spin`} />
                : <Icon className={`h-3.5 w-3.5 ${iconColor}`} />}
            </div>
            <div className="text-left">
              <p className="text-xs font-medium text-text-primary">{label}</p>
              <p className="text-[11px] text-text-muted">{sub}</p>
            </div>
          </button>
        ))}

        <div className="border-t border-border my-2" />

        {/* Coming soon */}
        <p className="px-1 pb-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider">Coming soon</p>
        {comingSoon.map(name => (
          <div key={name} className="flex items-center gap-3 px-2 py-2 rounded-lg opacity-50 cursor-not-allowed">
            <div className="h-8 w-8 rounded-lg bg-surface-elevated flex items-center justify-center shrink-0">
              <CloudUpload className="h-3.5 w-3.5 text-text-muted" />
            </div>
            <div className="flex items-center justify-between flex-1">
              <p className="text-xs font-medium text-text-secondary">{name}</p>
              <span className="text-[10px] text-text-muted bg-surface-elevated border border-border rounded-full px-2 py-0.5">Soon</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// --- File attachment chips ---

function AttachmentChips({ files, onRemove }: { files: FileAttachment[]; onRemove: (idx: number) => void }) {
  if (files.length === 0) return null
  const visual = files.filter(f => f.kind === 'image' || f.kind === 'video')
  const docs = files.filter(f => f.kind === 'document')
  return (
    <div className="flex flex-col gap-1.5 px-1 pb-1.5">
      {visual.length > 0 && (
        <div className="flex gap-2 flex-wrap">
          {visual.map((f) => {
            const globalIdx = files.indexOf(f)
            const src = f.data ? `data:${f.type};base64,${f.data}` : ''
            return (
              <div key={f.id} className="relative group shrink-0">
                {f.processing ? (
                  <div className="h-14 w-14 rounded-lg border border-border bg-surface-elevated flex items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                  </div>
                ) : f.error ? (
                  <div className="h-14 w-14 rounded-lg border border-danger/30 bg-danger/5 flex items-center justify-center text-[9px] text-danger text-center px-1">Failed</div>
                ) : f.kind === 'video' ? (
                  <video src={src} className="h-14 w-14 object-cover rounded-lg border border-border" muted playsInline />
                ) : (
                  <img src={src} alt={f.name} className="h-14 w-14 object-cover rounded-lg border border-border" />
                )}
                <button
                  onClick={() => onRemove(globalIdx)}
                  className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-danger text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity z-10"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] truncate px-1 py-0.5 rounded-b-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  {f.name}
                </div>
              </div>
            )
          })}
        </div>
      )}
      {docs.length > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {docs.map((f) => {
            const globalIdx = files.indexOf(f)
            return (
              <div key={f.id} className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded-full border text-xs',
                f.error ? 'bg-danger/5 border-danger/30 text-danger' : 'bg-accent/10 border-accent/20 text-text-secondary'
              )}>
                {f.processing ? <Loader2 className="h-3 w-3 animate-spin text-accent" /> : <FileText className="h-3 w-3 text-accent" />}
                <span className="max-w-[120px] truncate">{f.name}</span>
                <span className="text-text-muted">{f.error ? 'Failed' : f.processing ? 'Processing…' : formatFileSize(f.size)}</span>
                <button onClick={() => onRemove(globalIdx)} className="text-text-muted hover:text-danger ml-0.5" aria-label={`Remove ${f.name}`}><X className="h-3 w-3" /></button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// --- Response toolbar ---

function ResponseToolbar({
  content, message, isDocument, sections,
  onRetry, onRetryWithContext, onFeedback, onToggleExpand, expanded,
  hasPreview, onTogglePreview, previewOpen,
}: {
  content: string
  message: ChatMessage
  isDocument: boolean
  sections: { id: string; title: string; level: number }[]
  onRetry: () => void
  onRetryWithContext: () => void
  onFeedback: (type: 'positive' | 'negative', reason?: string, comment?: string) => void
  onToggleExpand: () => void
  expanded: boolean
  hasPreview: boolean
  onTogglePreview: () => void
  previewOpen: boolean
}) {
  const { copied: copiedText, copy: copyText } = useCopy()
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null)
  const [showExport, setShowExport] = useState(false)
  const exportRef = useRef<HTMLDivElement>(null)
  const thumbsUpRef = useRef<HTMLDivElement>(null)
  const thumbsDownRef = useRef<HTMLDivElement>(null)

  return (
    <div className="flex items-center gap-0.5 flex-wrap">
      <button onClick={() => copyText(content)} className="flex items-center gap-1 px-2 py-1 rounded text-[12px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors" title="Copy">
        {copiedText ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
        <span className="hidden sm:inline">{copiedText ? 'Copied' : 'Copy'}</span>
      </button>

      {(isDocument || hasPreview) && (
        <button onClick={() => downloadText(content, `ops-bridge-${Date.now()}.md`)} className="flex items-center gap-1 px-2 py-1 rounded text-[12px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors cursor-pointer" title="Download">
          <Download className="h-4 w-4" /><span className="hidden sm:inline">Download</span>
        </button>
      )}
      <button
        onClick={async () => {
          const sid = useAppStore.getState().activeSessionId
          const shareUrl = sid ? `${window.location.origin}/?session=${sid}` : window.location.origin
          if (navigator.share) {
            try { await navigator.share({ title: 'Ops-Bridge', text: content, url: shareUrl }) } catch { /* cancelled */ }
          } else {
            copyText(content + '\n\n' + shareUrl)
          }
        }}
        className="flex items-center gap-1 px-2 py-1 rounded text-[12px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors"
        title="Share"
      >
        <Share2 className="h-4 w-4" /><span className="hidden sm:inline">Share</span>
      </button>
      <div ref={exportRef}>
        <button onClick={() => setShowExport(!showExport)} className="flex items-center gap-1 px-2 py-1 rounded text-[12px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors" title="Save to">
          <CloudUpload className="h-4 w-4" /><span className="hidden sm:inline">Save to</span>
        </button>
        {showExport && (
          <PortalMenu anchorRef={exportRef} onClose={() => setShowExport(false)} direction="right">
            <SaveToPopover content={content} onClose={() => setShowExport(false)} />
          </PortalMenu>
        )}
      </div>
      <div className="w-px h-4 bg-white/10 mx-1" />
      <div ref={thumbsUpRef}>
        <button
          onClick={() => setFeedbackType(feedbackType === 'positive' ? null : 'positive')}
          className={cn('flex items-center gap-1 px-2 py-1 rounded text-[12px] transition-colors', message.feedback === 'positive' ? 'text-green-400 bg-green-400/10' : 'text-text-muted hover:text-green-400 hover:bg-green-400/5')}
          title="Good response"
        >
          <ThumbsUp className="h-4 w-4" />
        </button>
        {feedbackType === 'positive' && (
          <PortalMenu anchorRef={thumbsUpRef} onClose={() => setFeedbackType(null)} direction="right">
            <FeedbackModal
              type="positive"
              onSubmit={(reason, comment) => { onFeedback('positive', reason, comment); setFeedbackType(null) }}
              onClose={() => setFeedbackType(null)}
            />
          </PortalMenu>
        )}
      </div>
      <div ref={thumbsDownRef}>
        <button
          onClick={() => setFeedbackType(feedbackType === 'negative' ? null : 'negative')}
          className={cn('flex items-center gap-1 px-2 py-1 rounded text-[12px] transition-colors', message.feedback === 'negative' ? 'text-red-400 bg-red-400/10' : 'text-text-muted hover:text-red-400 hover:bg-red-400/5')}
          title="Bad response"
        >
          <ThumbsDown className="h-4 w-4" />
        </button>
        {feedbackType === 'negative' && (
          <PortalMenu anchorRef={thumbsDownRef} onClose={() => setFeedbackType(null)} direction="right">
            <FeedbackModal
              type="negative"
              onSubmit={(reason, comment) => { onFeedback('negative', reason, comment); setFeedbackType(null) }}
              onClose={() => setFeedbackType(null)}
            />
          </PortalMenu>
        )}
      </div>
      <div className="w-px h-4 bg-white/10 mx-1" />
      <button onClick={onRetry} className="flex items-center gap-1 px-2 py-1 rounded text-[12px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors" title="Retry">
        <RotateCw className="h-4 w-4" /><span className="hidden sm:inline">Retry</span>
      </button>
      <button onClick={onRetryWithContext} className="flex items-center gap-1 px-2 py-1 rounded text-[12px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors" title="Retry with context from this response">
        <RefreshCw className="h-4 w-4" /><span className="hidden sm:inline">Retry+</span>
      </button>
      {isDocument && (
        <>
          <div className="w-px h-4 bg-white/10 mx-1" />
          <button onClick={onToggleExpand} className="flex items-center gap-1 px-2 py-1 rounded text-[12px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors" title={expanded ? 'Collapse' : 'Expand'}>
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <SectionNav sections={sections} onJump={(id) => document.getElementById(`section-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        </>
      )}
      {hasPreview && (
        <>
          <div className="w-px h-4 bg-white/10 mx-1" />
          <button onClick={onTogglePreview} className={cn('flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors', previewOpen ? 'text-accent bg-accent/10' : 'text-text-muted hover:text-accent hover:bg-accent/5')} title={previewOpen ? 'Close Preview' : 'Preview'}>
            {previewOpen ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
            <span>{previewOpen ? 'Close Preview' : 'Preview'}</span>
          </button>
        </>
      )}
    </div>
  )
}

// --- User message bubble ---

function UserBubble({ message, onRetry, onEdit }: { message: ChatMessage; onRetry: () => void; onEdit: () => void }) {
  const { copied, copy } = useCopy()
  const attachmentMedia = mediaItemsFromAttachments(message.attachments)
  const legacyMedia = attachmentMedia.length > 0 ? [] : (message.images || []).map((img, i) => ({
    id: `legacy-${message.id}-${i}`,
    kind: 'image' as const,
    src: `data:${img.mimeType};base64,${img.data}`,
    filename: img.name,
    mimeType: img.mimeType,
  }))
  const mediaItems = [...attachmentMedia, ...legacyMedia]
  const documentAttachments = (message.attachments || []).filter((a) => a.kind === 'document')

  return (
    <div className="flex py-3 justify-end">
      <div className="flex flex-col items-end gap-1 max-w-[88%] sm:max-w-[75%] min-w-0">
        {mediaItems.length > 0 && <MediaGrid items={mediaItems} />}
        {documentAttachments.length > 0 && (
          <div className="flex flex-wrap justify-end gap-1.5">
            {documentAttachments.map((a) => (
              <span key={a.id || a.name} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-elevated px-2 py-1 text-[10px] text-text-secondary">
                <FileText className="h-3 w-3" />
                <span className="max-w-[160px] truncate">{a.name}</span>
                <span className="text-text-muted">{formatFileSize(a.size)}</span>
              </span>
            ))}
          </div>
        )}
        <div className="rounded-[1.35rem] px-4 py-2.5 text-sm leading-relaxed bg-surface-elevated text-text-primary break-words [overflow-wrap:anywhere]">
          {message.content}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => copy(message.content)}
            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors"
            title="Copy message"
          >
            {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
            <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
          </button>
          <button
            onClick={onEdit}
            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors"
            title="Edit and resend"
          >
            <Pencil className="h-3 w-3" />
            <span className="hidden sm:inline">Edit</span>
          </button>
          <button
            onClick={onRetry}
            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-text-muted hover:text-text-primary hover:bg-white/5 transition-colors"
            title="Resend this message"
          >
            <RotateCw className="h-3 w-3" />
            <span className="hidden sm:inline">Retry</span>
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Streaming typewriter reveal ---

function StreamingText({ content, active, onDone }: { content: string; active: boolean; onDone?: () => void }) {
  const [displayed, setDisplayed] = useState('')
  const displayedLenRef = useRef(0)
  const contentRef = useRef(content)
  const activeRef = useRef(active)
  const rafRef = useRef<number>(0)
  const runningRef = useRef(false)
  const lastTsRef = useRef<number>(0)
  const onDoneRef = useRef(onDone)

  // Keep latest values accessible to the RAF loop without restarting it on every chunk
  contentRef.current = content
  activeRef.current = active
  onDoneRef.current = onDone

  useEffect(() => {
    // Single persistent loop for the lifetime of this message instance.
    // It does NOT snap to full content when `active` (network streaming) goes false —
    // that was the bug: a long response finishes over the wire in ~1-2s while the
    // reveal is still on line 3, `active` flips false, and the old code dumped the
    // remaining text instantly. Now the loop keeps paced-revealing past that point
    // until displayed truly catches up to content, THEN fires onDone. If more
    // content arrives (active still true), it just keeps waiting/advancing.
    if (runningRef.current) return
    runningRef.current = true
    const CHARS_PER_SECOND = 45
    const advance = (ts: number) => {
      if (!lastTsRef.current) lastTsRef.current = ts
      const dt = ts - lastTsRef.current
      lastTsRef.current = ts
      const cur = displayedLenRef.current
      const target = contentRef.current
      if (cur < target.length) {
        const step = Math.max(1, Math.round((CHARS_PER_SECOND * dt) / 1000))
        const next = Math.min(cur + step, target.length)
        displayedLenRef.current = next
        setDisplayed(target.slice(0, next))
        rafRef.current = requestAnimationFrame(advance)
      } else if (activeRef.current) {
        // Caught up to what we have so far, but network stream still open — keep polling.
        lastTsRef.current = 0
        rafRef.current = requestAnimationFrame(advance)
      } else {
        // Caught up AND network stream has ended — genuinely finished.
        runningRef.current = false
        lastTsRef.current = 0
        onDoneRef.current?.()
      }
    }
    rafRef.current = requestAnimationFrame(advance)
  }, [])

  // Separate unmount-only cleanup so content changes never cancel the loop
  useEffect(() => {
    return () => { cancelAnimationFrame(rafRef.current) }
  }, [])

  // Plain text during streaming — no ReactMarkdown re-parsing on every character.
  // The outer AssistantResponse switches to <MarkdownContent> once streaming ends.
  // The trailing few characters render in a span keyed by length, so it remounts
  // on every new chunk and retriggers the fade-in animation at the writing edge —
  // the flat div previously had no element for the fade CSS to ever match.
  const FADE_TAIL = 6
  const hasTail = displayed.length > FADE_TAIL
  const stable = hasTail ? displayed.slice(0, -FADE_TAIL) : ''
  const tail = hasTail ? displayed.slice(-FADE_TAIL) : displayed
  return (
    <div className="whitespace-pre-wrap leading-relaxed text-text-secondary text-sm">
      {stable}
      <span key={displayedLenRef.current} className="stream-fade-char">{tail}</span>
    </div>
  )
}

// --- Assistant document card ---

function AssistantResponse({
  message, onApprove, onReject, onRetry, onRetryWithContext, onFeedback,
  onTogglePreview, previewOpen, previewMessageId, isStreaming,
}: {
  message: ChatMessage
  onApprove?: (id: string) => void
  onReject?: (id: string) => void
  onRetry: () => void
  onRetryWithContext: () => void
  onFeedback: (type: 'positive' | 'negative', reason?: string, comment?: string) => void
  onTogglePreview: (preview: ArtifactContent) => void
  previewOpen: boolean
  previewMessageId: string | null
  isStreaming?: boolean
}) {
  const [expanded, setExpanded] = useState(true)
  const standaloneHtml = useMemo(() => {
    const trimmed = message.content.trim()
    return /^<!doctype html/i.test(trimmed) || (/^<html(?:\s|>)/i.test(trimmed) && /<\/html>\s*$/i.test(trimmed))
  }, [message.content])
  const isDoc = useMemo(() => isDocumentResponse(message.content), [message.content])
  const sections = useMemo(() => extractSections(message.content), [message.content])
  const hasContent = message.content.length > 0
  const renderable = useMemo(() => detectArtifact(message.content, message.id, message.toolCalls), [message.content, message.id, message.toolCalls])
  const isPreviewActive = previewOpen && previewMessageId === message.id
  const messageMedia = useMemo(
    () => mergeMediaItems(
      mediaItemsFromToolCalls(message.toolCalls),
      mediaItemsFromAttachments(message.attachments),
    ),
    [message.attachments, message.toolCalls],
  )

  // Track whether this message ever streamed and whether the paced reveal has
  // actually finished catching up — do NOT key the Markdown/StreamingText switch
  // off isStreaming alone. That was the bug: isStreaming reflects the NETWORK
  // finishing, not the on-screen reveal finishing. For long responses the network
  // completes in ~1-2s while the reveal is still on line 3, so switching on
  // isStreaming=false dumped the remainder instantly. Now we wait for
  // StreamingText's own onDone, which only fires once displayed === content.
  const wasStreamingRef = useRef(false)
  const [revealComplete, setRevealComplete] = useState(false)
  if (isStreaming) wasStreamingRef.current = true
  const showStreamingReveal = wasStreamingRef.current && !revealComplete
  const trimmedContent = message.content.trim()
  const trivialMediaAck = messageMedia.length > 0
    && trimmedContent.length > 0
    && trimmedContent.length <= 180
    && /^(done\b|here\b|i(?:'ve| have) generated\b|your (?:generated )?(?:image|images)\b|generated (?:image|images)\b)/i.test(trimmedContent)
  const showContent = hasContent && !trivialMediaAck
  const toolCalls = message.toolCalls || []
  const mediaOnlyToolNames = new Set([
    'generate_image', 'get_image', 'get_image_status',
    'generate_video', 'get_video', 'get_video_status',
  ])
  const completedThinkingRequired =
    showContent
    || toolCalls.length > 1
    || toolCalls.some((tool) => !mediaOnlyToolNames.has(tool.name))

  return (
    <div className="py-3 min-w-0">
      {(message.thinkingActive || isStreaming) && (
        <ReasoningSummaryPanel
          reasoning={message.reasoning}
          toolCalls={message.toolCalls}
          active
          provider={message.provider}
          model={message.model}
          fallback={message.fallback}
          fallbackReason={message.fallbackReason}
        />
      )}
      {message.toolCalls && message.toolCalls.length > 0 && (
        <ToolCallsBar toolCalls={message.toolCalls} active={isStreaming} />
      )}
      {(messageMedia.length > 0 || (message.mediaProgress && messageMedia.length === 0)) && (
        <div className="mb-3 mt-1 w-full max-w-[620px]">
          {messageMedia.length > 0 ? (
            <MediaGrid items={messageMedia} />
          ) : (
            <div
              className="image-generating-placeholder aspect-square w-full max-w-[520px] rounded-[1.65rem] border border-accent/15"
              role="status"
              aria-label={message.mediaProgress?.kind === 'video' ? 'Generating video' : 'Generating image'}
            />
          )}
        </div>
      )}
      {message.persistenceWarning && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2 text-[11px] text-warning">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{message.persistenceWarning}</span>
        </div>
      )}
      {showContent && (
        <div className={cn(
          // No visible background or border -- text is contained by spacing
          // alone, not a box, matching how a response reads in this very
          // conversation: content just flows, grouped by margin, never a
          // rendered container. Kept the horizontal scroll behaviour for
          // wide content (code blocks, tables) since that's a functional
          // need, not a visual box choice.
          'text-sm text-text-primary max-w-full min-w-0 overflow-x-auto break-words',
          isDoc ? 'py-1' : 'py-1',
          showStreamingReveal && 'streaming-content',
        )}>
          {expanded ? (
            <div className={showStreamingReveal ? 'streaming-cursor' : undefined}>
              {standaloneHtml
                ? <HtmlContent html={message.content} />
                : showStreamingReveal
                  ? (
                    <StreamingText
                      content={message.content}
                      active={!!isStreaming}
                      onDone={() => setRevealComplete(true)}
                    />
                  )
                  : <MarkdownContent content={message.content} />
              }
            </div>
          ) : (
            <div className="text-text-muted text-xs italic">Response collapsed. Click expand to view.</div>
          )}
        </div>
      )}
      {message.approvalCard && (
        <InlineApprovalCard
          card={message.approvalCard}
          onApprove={() => onApprove?.(message.approvalCard!.id)}
          onReject={() => onReject?.(message.approvalCard!.id)}
        />
      )}
      {!isStreaming
        && !message.thinkingActive
        && completedThinkingRequired
        && ((message.reasoning?.length || 0) > 0 || toolCalls.length > 0) && (
        <div className="mt-3">
          <ReasoningSummaryPanel
            reasoning={message.reasoning}
            toolCalls={message.toolCalls}
            active={false}
            provider={message.provider}
            model={message.model}
            fallback={message.fallback}
            fallbackReason={message.fallbackReason}
          />
        </div>
      )}
      {showContent && (
        <div className="mt-2 pt-2 border-t border-transparent">
          <ResponseToolbar
            content={message.content}
            message={message}
            isDocument={isDoc}
            sections={sections}
            onRetry={onRetry}
            onRetryWithContext={onRetryWithContext}
            onFeedback={onFeedback}
            onToggleExpand={() => setExpanded(!expanded)}
            expanded={expanded}
            hasPreview={!!renderable}
            onTogglePreview={() => renderable && onTogglePreview(renderable)}
            previewOpen={isPreviewActive}
          />
        </div>
      )}
    </div>
  )
}

// --- Main panel ---

export function ChatPanel() {
  const [input, setInput] = useState('')
  const storeMessages = useAppStore((s) => s.chatMessages)
  const storeChatMessages = useAppStore((s) => s.setChatMessages)
  const activeSessionId = useAppStore((s) => s.activeSessionId)
  const activeSessionIdRef = useRef(activeSessionId)
  useEffect(() => { activeSessionIdRef.current = activeSessionId }, [activeSessionId])
  const [chatTitle, setChatTitle] = useState('Chat')
  const [chatMenuOpen, setChatMenuOpen] = useState(false)
  const chatMenuRef = useRef<HTMLDivElement>(null)
  const activeProjectId = useAppStore((s) => s.activeProjectId)
  const setTimerWidget = useAppStore((s) => s.setTimerWidget)
  const setActiveSessionId = useAppStore((s) => s.setActiveSessionId)
  const bumpSessionList = useAppStore((s) => s.bumpSessionList)
  const INIT_MSG: ChatMessage = { id: 'msg_init', role: 'system', content: 'Connected to Ops-Bridge. Live endpoint mode.', timestamp: new Date() }

  const [messages, setMessagesLocal] = useState<ChatMessage[]>(storeMessages.length > 0 ? storeMessages : [INIT_MSG])

  const setMessages = useCallback((updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
    setMessagesLocal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      storeChatMessages(next)
      return next
    })
  }, [storeChatMessages])

  useEffect(() => {
    if (!activeSessionId) {
      setMessages([INIT_MSG])
      return
    }
    fetch(`/api/dashboard/sessions/${activeSessionId}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.session?.title) setChatTitle(data.session.title)
        if (data.messages && data.messages.length > 0) {
          const loaded: ChatMessage[] = data.messages.map((m: {
            id: string
            role: string
            content: string
            created_at: string
            attachments?: MessageAttachment[]
            toolCalls?: ChatMessage['toolCalls']
            reasoning?: ChatMessage['reasoning']
            provider?: string
            model?: string
          }) => ({
            id: m.id,
            role: m.role as 'user' | 'assistant' | 'system',
            content: m.content,
            timestamp: new Date(m.created_at),
            attachments: Array.isArray(m.attachments) ? m.attachments : undefined,
            toolCalls: Array.isArray(m.toolCalls) ? m.toolCalls : undefined,
            reasoning: Array.isArray(m.reasoning) ? m.reasoning : undefined,
            provider: m.provider,
            model: m.model,
          }))
          // Only replace local state if D1 has at least as many messages.
          // If D1 has fewer, the in-memory state is newer (in-flight saves
          // haven't landed yet) — keep the local version to avoid losing the
          // last message when returning to chat quickly.
          setMessages(prev => {
            const prevReal = prev.filter(m => m.role !== 'system').length
            return loaded.length >= prevReal ? loaded : prev
          })
        }
        // Don't reset to INIT_MSG on empty — local state may have unsaved messages
      })
      .catch(() => {})
  }, [activeSessionId])

  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const streamIdRef = useRef<string>('')
  const abortControllerRef = useRef<AbortController | null>(null)
  const streamReasoningRef = useRef<Array<{decision: string; reason: string; confidence: number}>>([])
  const [devConsoleOpen, setDevConsoleOpen] = useState(false)
  const [adminConsoleAllowed, setAdminConsoleAllowed] = useState(false)
  useEffect(() => {
    let active = true
    const token = getToken()
    setAdminConsoleAllowed(false)
    api.capabilityView().then(view => {
      if (!active || getToken() !== token) return
      const permitted = view.super_admin === true
      setAdminConsoleAllowed(permitted)
      if (!permitted) setDevConsoleOpen(false)
    }).catch(() => {
      if (active) { setAdminConsoleAllowed(false); setDevConsoleOpen(false) }
    })
    return () => { active = false }
  }, [chatMenuOpen])
  // Presentation only; every operation still passes backend authorization,
  // approval and Worker Lock. Recheck before dispatching any console shortcut.
  const handleAdminAction = async (prompt: string) => {
    const token = getToken()
    try {
      const view = await api.capabilityView()
      if (getToken() !== token || view.super_admin !== true) throw new Error('Access unavailable')
      handleQuickAction(prompt)
    } catch {
      setAdminConsoleAllowed(false)
      setDevConsoleOpen(false)
      setError('Administration access could not be verified. No action was submitted.')
    }
  }

  const [previewContent, setPreviewContent] = useState<ArtifactContent | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [attachments, setAttachments] = useState<FileAttachment[]>([])
  const [dragOver, setDragOver] = useState(false)
  const DEFAULT_MODE = 'ops-1'
  const [chatMode, setChatMode] = useState<string>(DEFAULT_MODE)
  // Two complementary sources, not a replacement of one by the other --
  // caught this distinction via a real TypeScript error after an initial
  // wrong assumption that this state was dead. opsActualModel is set live
  // from the provider_selected SSE event as each response actually streams
  // in -- the most accurate signal once at least one message has been
  // sent this session. modelStatus (below) is a periodic poll of what's
  // currently configured, useful as the value before any message exists
  // yet, or if the SSE-derived value is still null for any reason.
  const [opsActualModel, setOpsActualModel] = useState<string | null>(null)
  const { data: modelStatus } = useModelStatus()

  // AD-024 dynamic logical model roles. The selector never hardcodes the
  // current OPS model IDs: it renders the live registry exposed by
  // /dashboard/model-status and therefore follows future model promotions
  // without another frontend release.
  const modelRole = (role: string) => modelStatus?.roles?.find((entry) => entry.role === role)
  const toolOrchestratorRole = modelRole('tool_orchestrator')
  const deepReasonerRole = modelRole('deep_reasoner')
  const fastGeneralRole = modelRole('fast_general')
  const visionRole = modelRole('vision')
  const verifierRole = modelRole('verifier')
  const toolOrchestratorModel = toolOrchestratorRole?.active?.model_id || modelStatus?.capable?.model || null
  const deepReasonerModel = deepReasonerRole?.active?.model_id || null
  const fastGeneralModel = fastGeneralRole?.active?.model_id || modelStatus?.fast?.model || null
  const visionModel = visionRole?.active?.model_id || null
  const verifierModel = verifierRole?.active?.model_id || null
  const toolFallbackModels = (toolOrchestratorRole?.fallbacks || []).map((entry) => entry.model_id)
  const deepFallbackModels = (deepReasonerRole?.fallbacks || []).map((entry) => entry.model_id)
  const opsFallbackModels = Array.from(new Set([...toolFallbackModels, ...deepFallbackModels]))

  const [modeLabels, setModeLabels] = useState<Record<string, string>>({})
  const [modelMenuOpen, setModelMenuOpen] = useState(false)
  const [attachMenuOpen, setAttachMenuOpen] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)
  const filesInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const modelMenuRef = useRef<HTMLDivElement>(null)
  const attachMenuRef = useRef<HTMLDivElement>(null)
  // Genuinely dead: useApprove/useReject called the stale /dashboard/approve
  // endpoint and were never actually used anywhere except here. Removed
  // along with their import, rather than left in place unused --
  // handleApprove/handleReject below now call api.approveAction/denyAction
  // directly, the same canonical approval endpoints used by the Governance Approval Centre.

  const handleTogglePreview = useCallback((preview: ArtifactContent) => {
    if (previewOpen && previewContent?.messageId === preview.messageId) {
      setPreviewOpen(false)
      setPreviewContent(null)
    } else {
      setPreviewContent(preview)
      setPreviewOpen(true)
    }
  }, [previewOpen, previewContent])

  // Direct instruction: a way to bring a closed preview back that lives
  // near the input, not just a small per-message toolbar icon (which does
  // already exist -- hasPreview/onTogglePreview -- but the text label is
  // hidden on mobile via `hidden sm:inline`, leaving just a bare eye icon
  // that's easy to miss entirely). Scans backward for the most recent
  // message with a real, detectable artifact, regardless of whether the
  // canvas is currently open for it.
  const latestArtifact = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      const found = detectArtifact(m.content, m.id, m.toolCalls)
      if (found) return found
    }
    return null
  }, [messages])

  // Auto-open canvas: when streaming ends and the latest assistant message contains
  // an artifact, open the canvas automatically (Claude.ai style).
  // Also tracks canvas content when already open.
  useEffect(() => {
    const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant' && m.content)
    if (!lastAssistant) return
    const artifact = detectArtifact(lastAssistant.content, lastAssistant.id, lastAssistant.toolCalls)
    if (artifact) {
      if (!previewOpen) {
        // Auto-open on first artifact detection (only when not already streaming)
        if (!streaming) {
          setPreviewContent(artifact)
          setPreviewOpen(true)
        }
      } else if (artifact.messageId !== previewContent?.messageId) {
        // Update canvas to latest artifact when already open
        setPreviewContent(artifact)
      }
    }
  }, [messages, streaming])

  const processFiles = useCallback((incoming: File[]) => {
    const availableSlots = Math.max(0, MAX_FILES - attachments.length)
    if (availableSlots === 0) {
      setError(`Maximum ${MAX_FILES} files allowed`)
      return
    }

    const existingBytes = attachments.reduce((sum, a) => sum + a.size, 0)
    let acceptedBytes = 0
    const accepted: File[] = []
    for (const file of incoming.slice(0, availableSlots)) {
      if (file.size > MAX_FILE_SIZE) {
        setError(`File ${file.name} exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`)
        continue
      }
      if (existingBytes + acceptedBytes + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
        setError(`Attachments are limited to ${formatFileSize(MAX_TOTAL_ATTACHMENT_BYTES)} total`)
        break
      }
      accepted.push(file)
      acceptedBytes += file.size
    }

    if (incoming.length > availableSlots) setError(`Maximum ${MAX_FILES} files allowed`)
    if (accepted.length === 0) return

    const jobs = accepted.map((file, index) => {
      const ext = file.name.split('.').pop()?.toLowerCase() || ''
      const isImage = IMAGE_TYPES.includes(file.type) || (!file.type && ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext))
      const isVideo = VIDEO_TYPES.includes(file.type) || (!file.type && ['mp4', 'webm'].includes(ext))
      const kind: FileAttachment['kind'] = isImage ? 'image' : isVideo ? 'video' : 'document'
      const id = `pending-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`
      return { file, id, kind, isImage }
    })

    setAttachments(prev => [
      ...prev,
      ...jobs.map(({ file, id, kind, isImage }) => ({
        id,
        name: file.name,
        size: file.size,
        type: file.type || (kind === 'image' ? 'image/jpeg' : kind === 'video' ? 'video/mp4' : 'application/octet-stream'),
        kind,
        data: '',
        isImage,
        processing: true,
      })),
    ])

    jobs.forEach(async ({ file, id, kind, isImage }) => {
      try {
        const rawData = await fileToBase64(file)
        let data = rawData
        if (kind === 'document') {
          const parsed = await extractDocumentText(file)
          data = parsed.text
          if (!data.trim()) throw new Error('No readable text could be extracted')
          if (parsed.truncated) data += '\n\n[Document text truncated to 200,000 characters for chat context.]'
        }
        setAttachments(prev => prev.map(a => a.id === id ? {
          ...a,
          data,
          rawData,
          processing: false,
          error: undefined,
          isImage,
        } : a))
      } catch (e) {
        setAttachments(prev => prev.map(a => a.id === id ? {
          ...a,
          processing: false,
          error: e instanceof Error ? e.message : 'Unable to process attachment',
        } : a))
      }
    })
  }, [attachments])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    processFiles(Array.from(files))
    e.target.value = ''
  }, [processFiles])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    processFiles(Array.from(e.dataTransfer.files))
  }, [processFiles])

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedFiles = Array.from(e.clipboardData.files || [])
    if (pastedFiles.length === 0) return
    e.preventDefault()
    processFiles(pastedFiles)
  }, [processFiles])

  const hydrateMessageAttachments = useCallback(async (items?: MessageAttachment[]): Promise<FileAttachment[]> => {
    if (!items?.length) return []
    return Promise.all(items.map(async (a, index) => {
      let rawData = a.data || ''
      if (!rawData) {
        const generatedSourceId = typeof a.metadata?.sourceId === 'string' ? a.metadata.sourceId : ''
        const candidateUrls = [
          ...(a.kind === 'image' && /^IMG-[A-Za-z0-9-]+$/.test(generatedSourceId)
            ? [`/api/dashboard/images/${encodeURIComponent(generatedSourceId)}`]
            : []),
          ...(a.url ? [a.url] : []),
        ]

        let lastError: Error | null = null
        for (const url of candidateUrls) {
          try {
            const response = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            const blob = await response.blob()
            rawData = await fileToBase64(new File([blob], a.name, { type: a.mimeType || blob.type }))
            if (rawData) break
          } catch (error) {
            lastError = error instanceof Error ? error : new Error('Attachment fetch failed')
          }
        }

        if (!rawData && a.kind !== 'document') {
          throw new Error(`Unable to reload ${a.name}${lastError ? `: ${lastError.message}` : ''}`)
        }
      }

      return {
        id: a.id || `rehydrated-${Date.now()}-${index}`,
        name: a.name,
        size: a.size,
        type: a.mimeType,
        kind: a.kind,
        data: a.kind === 'document' ? (a.extractedText || '') : rawData,
        rawData,
        isImage: a.kind === 'image',
        processing: false,
      }
    }))
  }, [])

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streaming])

  useEffect(() => {
    if (!modelMenuOpen && !attachMenuOpen) return
    const handler = (e: MouseEvent) => {
      if (modelMenuOpen && modelMenuRef.current && !modelMenuRef.current.contains(e.target as Node)) setModelMenuOpen(false)
      if (attachMenuOpen && attachMenuRef.current && !attachMenuRef.current.contains(e.target as Node)) setAttachMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [modelMenuOpen, attachMenuOpen])

  useEffect(() => {
    fetch('/api/dashboard/chat/modes', { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.modes) {
          const map: Record<string, string> = {}
          for (const m of data.modes) if (m.mode && m.label) map[m.mode] = m.label
          setModeLabels(map)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && streaming && streamIdRef.current) {
        fetch(`/api/dashboard/stream/resume/${streamIdRef.current}`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        }).catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [streaming])

  const streamChat = useCallback(async (userContent: string, modeOverride?: string, continueFrom?: unknown[], imageAttachments?: Array<{ name: string; data: string; mimeType: string }>) => {
    const controller = new AbortController()
    abortControllerRef.current = controller
    setStreaming(true)
    setError(null)
    const sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
    streamIdRef.current = sid
    streamReasoningRef.current = []

    const history: Array<{ role: string; content: unknown }> = []
    const recentMessages = messages.filter((m) => m.role !== 'system').slice(-20)

    // Historical USER uploads can stay in their original user turn. Generated
    // ASSISTANT media is different: some provider chat schemas do not reliably
    // accept image blocks on assistant-role messages. Keep assistant turns as
    // text, then carry the actual pixels into the next relevant USER turn.
    for (const m of recentMessages) {
      if (m.role === 'user' && m.attachments?.some(a => a.kind === 'image')) {
        try {
          const restored = await hydrateMessageAttachments(m.attachments)
          const priorImages = restored.filter(a => a.kind === 'image' && a.data)
          if (priorImages.length > 0) {
            history.push({
              role: m.role,
              content: [
                ...priorImages.map(img => ({
                  type: 'image',
                  source: { type: 'base64', media_type: img.type || 'image/jpeg', data: img.data },
                })),
                { type: 'text', text: m.content },
              ],
            })
            continue
          }
        } catch {
          // If a historical user attachment can no longer be loaded, preserve the text turn.
        }
      }
      history.push({ role: m.role, content: m.content })
    }

    const lastConversationMessage = recentMessages[recentMessages.length - 1]
    const immediatelyFollowsGeneratedImages =
      lastConversationMessage?.role === 'assistant' &&
      !!lastConversationMessage.attachments?.some((attachment) => attachment.kind === 'image')

    const refersToPriorVisuals =
      /\b(image|images|photo|photos|picture|pictures|logo|logos|design|designs|visual|visuals|graphic|graphics|generated image|generated images|which one|which is better|which is best|compare|comparison|both images|both designs|first image|second image|first one|second one)\b/i.test(userContent)

    const assistantImageContext: Array<{ name: string; data: string; mimeType: string }> = []

    if (!continueFrom && (immediatelyFollowsGeneratedImages || refersToPriorVisuals)) {
      const assistantMediaMessages = recentMessages
        .filter((message) => message.role === 'assistant' && message.attachments?.some((attachment) => attachment.kind === 'image'))
        .slice(-3)

      for (const message of assistantMediaMessages) {
        try {
          const restored = await hydrateMessageAttachments(message.attachments)
          for (const image of restored.filter((attachment) => attachment.kind === 'image' && attachment.data)) {
            assistantImageContext.push({
              name: image.name,
              data: image.data,
              mimeType: image.type || 'image/jpeg',
            })
          }
        } catch {
          // Keep the assistant text history if one historical visual cannot be recovered.
        }
      }

      if (assistantImageContext.length > 4) {
        assistantImageContext.splice(0, assistantImageContext.length - 4)
      }
    }

    // On a Continue action, userContent is empty and the task resumes purely
    // from continueFrom's own raw conversation state -- don't push an empty
    // synthetic user turn onto the simplified history in that case.
    if (!continueFrom) {
      const currentUploads = imageAttachments || []

      if (currentUploads.length > 0 || assistantImageContext.length > 0) {
        // Keep the user's actual request as the FIRST text block. Core uses the
        // first user text block for capability routing, so putting visual-context
        // metadata first prevented requests like "create an image" from reaching
        // the FULL_AGENTIC tool path even though generate_image was available.
        const visionContent: Array<{ type: string; source?: { type: string; media_type: string; data: string }; text?: string }> = [{
          type: 'text',
          text: userContent || 'Describe and analyse the supplied visual context.',
        }]

        if (assistantImageContext.length > 0) {
          visionContent.push({
            type: 'text',
            text: 'Visual context from images you generated earlier in this conversation follows. These are real image inputs for this turn. Inspect the actual pixels when the user refers to them. If a specific image cannot be decoded, say that specific image could not be read.',
          })

          assistantImageContext.forEach((img, index) => {
            visionContent.push({ type: 'text', text: `Previously generated image ${index + 1}: ${img.name}` })
            visionContent.push({
              type: 'image',
              source: { type: 'base64', media_type: img.mimeType, data: img.data },
            })
          })
        }

        if (currentUploads.length > 0) {
          visionContent.push({
            type: 'text',
            text: assistantImageContext.length > 0
              ? 'Images uploaded with the current user message follow. These are real visual inputs; inspect the pixels directly.'
              : 'The following are real image inputs supplied with the current user message. Inspect the pixels directly. If a specific image cannot be decoded, report that specific image failure rather than claiming OPS has no vision capability.',
          })
          currentUploads.forEach((img) => {
            visionContent.push({
              type: 'image',
              source: { type: 'base64', media_type: img.mimeType, data: img.data },
            })
          })
        }

        history.push({ role: 'user', content: visionContent as unknown as string })
      } else {
        history.push({ role: 'user', content: userContent })
      }
    }

    const assistantId = `msg_${Date.now()}_assistant`
    setMessages((prev) => [...prev, { id: assistantId, role: 'assistant', content: '', timestamp: new Date(), thinkingActive: true }])

    try {
      const res = await api.chatStream(history, modeOverride ?? chatMode, sid, continueFrom, imageAttachments, controller.signal)
      if (!res.ok) {
        const errText = await res.text()
        setError(`API error: ${res.status}`)
        setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: `Error: ${errText}` } : m))
        setStreaming(false)
        return
      }

      const reader = res.body?.getReader()
      if (!reader) { setStreaming(false); return }

      const decoder = new TextDecoder()
      let accumulated = ''
      let thinkingText = 'Reviewing the request and deciding what information or tools are needed.'
      const toolCalls: Record<string, ChatMessage['toolCalls']> = {}
      const getToolCalls = () => Object.values(toolCalls).flat().filter(Boolean) as NonNullable<ChatMessage['toolCalls']>[number][]
      let providerInfo: { provider?: string; model?: string; fallback?: boolean; fallbackReason?: string } = {}
      let sseBuffer = ''
      let doneReceived = false

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        sseBuffer += decoder.decode(value, { stream: true })
        const lines = sseBuffer.split('\n')
        sseBuffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (data === '[DONE]') { doneReceived = true; continue }

          try {
            const parsed = JSON.parse(data)

            if (parsed.type === 'thinking_start') {
              thinkingText = 'Reviewing the request and deciding what information or tools are needed.'
              setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, thinking: thinkingText, thinkingActive: true } : m))
            } else if (parsed.type === 'thinking_delta') {
              // Raw provider chain-of-thought is intentionally not exposed.
              // The UI shows concise decision/tool summaries instead.
              setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, thinking: thinkingText, thinkingActive: true } : m))
            } else if (parsed.type === 'thinking_stop') {
              setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, thinking: thinkingText, thinkingActive: false } : m))
            } else if (parsed.type === 'content_block_delta' && parsed.delta?.text) {
              accumulated += parsed.delta.text
              setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: accumulated, toolCalls: getToolCalls() } : m))
            } else if (parsed.type === 'tool_start') {
              const tsKey = parsed.tool_use_id || ('ts_' + parsed.name)
              toolCalls[tsKey] = [{ id: tsKey, name: parsed.name, status: 'running' as const }]
              const toolTrace = {
                decision: 'tool selection',
                reason: TOOL_PURPOSES[String(parsed.name)] || `Using ${String(parsed.name)} because the request requires that capability.`,
                confidence: 1,
              }
              streamReasoningRef.current = [...streamReasoningRef.current, toolTrace]
              const isMediaGeneration = parsed.name === 'generate_image' || parsed.name === 'generate_video'
              setMessages((prev) => prev.map((m) => m.id === assistantId ? {
                ...m,
                toolCalls: getToolCalls(),
                reasoning: streamReasoningRef.current,
                thinkingActive: true,
                ...(isMediaGeneration ? {
                  mediaProgress: {
                    kind: parsed.name === 'generate_video' ? 'video' as const : 'image' as const,
                    percent: 25,
                    stage: parsed.name === 'generate_video'
                      ? 'Generation request accepted; the video provider is rendering.'
                      : 'Generation request accepted; the image provider is rendering.',
                  },
                } : {}),
              } : m))
            } else if (parsed.type === 'approval_required' && parsed.id) {
              setMessages((prev) => prev.map((m) => m.id === assistantId ? {
                ...m,
                approvalCard: { id: String(parsed.id), action: String(parsed.action || ''), tier: Number(parsed.tier) || 2, details: String(parsed.details || ''), status: 'pending' as const },
              } : m))
            } else if (parsed.type === 'tool_complete') {
              const tcKey = parsed.tool_use_id || ('ts_' + parsed.name)
              toolCalls[tcKey] = [{
                id: tcKey,
                name: parsed.name,
                status: parsed.status === 'success' ? 'success' as const : 'error' as const,
                duration: parsed.duration,
                result: normaliseToolResultString(parsed.result),
              }]
              const mediaCompleted = ['generate_image', 'get_image', 'generate_video', 'get_video'].includes(parsed.name) && parsed.status === 'success'
              const mediaFailed = ['generate_image', 'get_image', 'generate_video', 'get_video'].includes(parsed.name) && parsed.status !== 'success'
              setMessages((prev) => prev.map((m) => m.id === assistantId ? {
                ...m,
                toolCalls: getToolCalls(),
                ...(mediaCompleted ? {
                  mediaProgress: {
                    kind: parsed.name.includes('video') ? 'video' as const : 'image' as const,
                    percent: 85,
                    stage: 'Generation completed; preparing the media for this conversation.',
                  },
                } : {}),
                ...(mediaFailed ? { mediaProgress: undefined } : {}),
              } : m))
              if (['start_timer', 'stop_timer', 'reset_timer', 'get_timer_status'].includes(parsed.name) && parsed.status === 'success' && parsed.result) {
                try {
                  const tr = typeof parsed.result === 'string' ? JSON.parse(parsed.result) : parsed.result
                  setTimerWidget({
                    open: tr.status !== 'none',
                    status: tr.status === 'started' || tr.status === 'already_running' ? 'running' : tr.status === 'reset' ? (tr.running ? 'running' : 'stopped') : (tr.status === 'running' ? 'running' : 'stopped'),
                    label: tr.label ?? null,
                    elapsedMs: tr.elapsed_ms ?? 0,
                    lastServerUpdate: Date.now(),
                  })
                } catch { /* ignore malformed timer result */ }
              }
            } else if (parsed.type === 'tool_status') {
              toolCalls[parsed.tool_use_id] = [{
                id: parsed.tool_use_id,
                name: parsed.name,
                status: parsed.status === 'running' ? 'running' as const : (parsed.status === 'success' || parsed.status === 'done') ? 'success' as const : 'error' as const,
                duration: parsed.duration,
                result: normaliseToolResultString(parsed.result),
              }]
              const mediaStatusEvent = ['generate_image', 'get_image', 'generate_video', 'get_video'].includes(parsed.name)
              const mediaStatusSuccess = parsed.status === 'success' || parsed.status === 'done'
              setMessages((prev) => prev.map((m) => m.id === assistantId ? {
                ...m,
                toolCalls: getToolCalls(),
                ...(mediaStatusEvent && parsed.status === 'running' ? {
                  mediaProgress: {
                    kind: parsed.name.includes('video') ? 'video' as const : 'image' as const,
                    percent: 25,
                    stage: parsed.name.includes('video')
                      ? 'Generation request accepted; the video provider is rendering.'
                      : 'Generation request accepted; the image provider is rendering.',
                  },
                } : {}),
                ...(mediaStatusEvent && mediaStatusSuccess ? {
                  mediaProgress: {
                    kind: parsed.name.includes('video') ? 'video' as const : 'image' as const,
                    percent: 85,
                    stage: 'Generation completed; preparing the media for this conversation.',
                  },
                } : {}),
                ...(mediaStatusEvent && !mediaStatusSuccess && parsed.status !== 'running' ? { mediaProgress: undefined } : {}),
              } : m))
            } else if (parsed.type === 'provider_selected') {
              const fb = parsed.fallback ? { fallback: true, fallbackReason: parsed.label as string | undefined } : {}
              providerInfo = { provider: parsed.provider, model: parsed.model, ...fb }
              const modelTrace = {
                decision: 'model selection',
                reason: `Using ${String(parsed.provider || 'OPS')}${parsed.model ? ` / ${String(parsed.model)}` : ''} for this response.`,
                confidence: 1,
              }
              if (!streamReasoningRef.current.some(r => r.decision === modelTrace.decision && r.reason === modelTrace.reason)) {
                streamReasoningRef.current = [...streamReasoningRef.current, modelTrace]
                }
              setMessages((prev) => prev.map((m) => m.id === assistantId ? {
                ...m,
                provider: parsed.provider,
                model: parsed.model,
                reasoning: streamReasoningRef.current,
                ...fb,
              } : m))
              if (parsed.provider === 'ops' && parsed.model) {
                const inner = parsed.model.match(/\(([^)]+)\)$/)
                setOpsActualModel(inner ? inner[1] : parsed.model)
              }
            } else if (parsed.type === 'reasoning' && parsed.decision) {
              const trace = { decision: String(parsed.decision), reason: String(parsed.reason || ''), confidence: Number(parsed.confidence || 0) }
              streamReasoningRef.current = [...streamReasoningRef.current, trace]
              setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, reasoning: streamReasoningRef.current } : m))
            } else if (parsed.type === 'limit_reached') {
              setMessages((prev) => prev.map((m) => m.id === assistantId ? {
                ...m,
                continuationState: {
                  conversation: parsed.conversation || [],
                  reason: parsed.reason === 'cost' ? 'cost' as const : 'turns' as const,
                  turnsUsed: Number(parsed.turns_used || 0),
                  costUsd: Number(parsed.cost_usd || 0),
                  costCapUsd: Number(parsed.cost_cap_usd || 0),
                },
              } : m))
            }
          } catch { /* skip non-JSON lines */ }
        }
      }

      if (!doneReceived && accumulated.length > 0) {
        await new Promise<void>((r) => setTimeout(r, 2000))
        try {
          const resumeRes = await fetch(`/api/dashboard/stream/resume/${sid}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          })
          if (resumeRes.ok) {
            const resumeData = await resumeRes.json()
            if (resumeData.resumed && resumeData.accumulated_text) {
              accumulated = resumeData.accumulated_text
              setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: accumulated } : m))
            }
          }
        } catch { /* use what we have */ }
      }
      const finalToolCalls = getToolCalls()
      const generatedMedia = mediaItemsFromToolCalls(finalToolCalls)
      const assistantAttachmentRequests = generatedMedia
        .filter(item => item.kind === 'image' && !!item.sourceId)
        .map(item => ({
          kind: item.kind,
          name: item.filename,
          mimeType: item.mimeType || 'image/png',
          sourceImageId: item.sourceId,
          metadata: {
            sourceId: item.sourceId,
            prompt: item.prompt,
            model: item.model,
            size: item.dimensions,
            createdAt: item.createdAt,
          },
        }))
      const finalContent = accumulated || (generatedMedia.length > 0 ? 'Here is the generated media.' : '')
      const final: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: finalContent,
        timestamp: new Date(),
        toolCalls: finalToolCalls,
        reasoning: streamReasoningRef.current.slice(),
        thinkingActive: false,
        ...providerInfo,
      }
      setMessages((prev) => prev.map((m) => m.id === assistantId ? final : m))

      // Persist the assistant's visible result, generated media references and
      // safe execution summary together. Generated image bytes are copied
      // server-side into chat-owned R2 storage so old conversations do not
      // depend on a temporary browser URL or on the Image Library remaining intact.
      const sessionId = activeSessionIdRef.current
      if (sessionId && (finalContent.trim() || assistantAttachmentRequests.length > 0)) {
        try {
          const persistedRes = await fetch(`/api/dashboard/sessions/${sessionId}/messages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
            body: JSON.stringify({
              role: 'assistant',
              content: finalContent,
              attachments: assistantAttachmentRequests,
              runtime: {
                provider: providerInfo.provider,
                model: providerInfo.model,
                toolCalls: finalToolCalls,
                reasoning: streamReasoningRef.current,
              },
            }),
          })
          if (!persistedRes.ok) throw new Error(`Message persistence failed (${persistedRes.status})`)
          const persistedData = await persistedRes.json()
          setMessages(prev => prev.map(m => m.id === assistantId ? {
            ...m,
            attachments: Array.isArray(persistedData.attachments) && persistedData.attachments.length > 0
              ? persistedData.attachments
              : m.attachments,
            persistenceWarning: undefined,
          } : m))
        } catch {
          setMessages(prev => prev.map(m => m.id === assistantId ? {
            ...m,
            persistenceWarning: generatedMedia.length > 0
              ? 'The generated media is visible, but OPS could not persist its chat copy. It may not survive a future reload.'
              : 'OPS could not persist this response history entry.',
          } : m))
        }
      }
      // Auto-title session from first user message
      const priorUserCount = messages.filter(m => m.role === 'user').length
      if (sessionId && priorUserCount === 0 && userContent.trim()) {
        const autoTitle = userContent.trim().replace(/\n/g, ' ').slice(0, 50)
        fetch(`/api/dashboard/sessions/${sessionId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ title: autoTitle }),
        }).catch(() => {})
        bumpSessionList()
      }
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: m.content || '*(stopped)*' } : m))
      } else {
        const msg = e instanceof Error ? e.message : 'Unknown error'
        setError(msg)
        setMessages((prev) => prev.map((m) => m.id === assistantId ? { ...m, content: `Connection error: ${msg}` } : m))
      }
    } finally {
      abortControllerRef.current = null
      setStreaming(false)
    }
  }, [messages, setMessages, chatMode, activeSessionId, bumpSessionList, hydrateMessageAttachments])

  const handleSend = async () => {
    if ((!input.trim() && attachments.length === 0) || streaming) return
    if (attachments.some(a => a.processing)) {
      setError('Please wait for attachments to finish processing.')
      return
    }
    if (attachments.some(a => a.error)) {
      setError('Remove failed attachments before sending.')
      return
    }

    let userContent = input.trim()
    const documentAttachments = attachments.filter(a => a.kind === 'document')
    if (!userContent && attachments.length > 0) {
      userContent = documentAttachments.length > 0
        ? 'Please review the attached document.'
        : attachments.some(a => a.kind === 'image')
          ? 'Please review the attached image.'
          : 'Please review the attached media.'
    }

    if (documentAttachments.length > 0) {
      for (const a of documentAttachments) {
        userContent += `\n\n\`\`\`text\n// Extracted from ${a.name}\n${a.data}\n\`\`\``
      }
    }

    const imageAttachments = attachments
      .filter(a => a.kind === 'image')
      .map(a => ({ name: a.name, data: a.data, mimeType: a.type || 'image/jpeg' }))

    const messageAttachments: MessageAttachment[] = attachments.map(a => ({
      kind: a.kind,
      name: a.name,
      mimeType: a.type || (a.kind === 'image' ? 'image/jpeg' : a.kind === 'video' ? 'video/mp4' : 'application/octet-stream'),
      size: a.size,
      data: a.kind === 'image' || a.kind === 'video' ? a.data : undefined,
      extractedText: a.kind === 'document' ? a.data : undefined,
    }))

    const userMsg: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: userContent,
      timestamp: new Date(),
      ...(imageAttachments.length > 0 ? { images: imageAttachments } : {}),
      attachments: messageAttachments,
    }
    setMessages((prev) => [...prev, userMsg])

    // Ensure a session exists before saving — auto-create if none
    let currentSessionId = activeSessionIdRef.current
    if (!currentSessionId) {
      try {
        const r = await fetch('/api/dashboard/sessions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
          body: JSON.stringify({ title: userContent.trim().replace(/\n/g, ' ').slice(0, 50) || 'New Chat' }),
        })
        const data = await r.json()
        if (data.session?.id) {
          currentSessionId = data.session.id
          activeSessionIdRef.current = currentSessionId
          setActiveSessionId(currentSessionId)
          bumpSessionList()
        }
      } catch {}
    }

    if (currentSessionId) {
      const persistencePayload = attachments.map(a => ({
        kind: a.kind,
        name: a.name,
        mimeType: a.type || 'application/octet-stream',
        size: a.size,
        rawData: a.rawData || a.data,
        extractedText: a.kind === 'document' ? a.data : undefined,
      }))
      fetch(`/api/dashboard/sessions/${currentSessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ role: 'user', content: userContent, attachments: persistencePayload }),
      })
        .then(r => r.ok ? r.json() : Promise.reject(new Error(`Message persistence failed (${r.status})`)))
        .then(data => {
          if (!Array.isArray(data.attachments) || data.attachments.length === 0) return
          setMessages(prev => prev.map(m => m.id === userMsg.id ? { ...m, attachments: data.attachments } : m))
        })
        .catch(() => {})
    }

    const content = userContent
    setInput('')
    setAttachments([])
    if (textareaRef.current) { textareaRef.current.style.height = '52px' }
    streamChat(content, undefined, undefined, imageAttachments.length > 0 ? imageAttachments : undefined)
  }

  const handleStop = () => {
    abortControllerRef.current?.abort()
  }

  const handleEdit = async (messageId: string) => {
    const idx = messages.findIndex(m => m.id === messageId)
    if (idx === -1) return
    const msg = messages[idx]
    setInput(msg.content)
    try {
      setAttachments(await hydrateMessageAttachments(msg.attachments))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to reload message attachments')
      setAttachments([])
    }
    setMessages(prev => prev.slice(0, idx))
    if (textareaRef.current) {
      textareaRef.current.focus()
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  const handleQuickAction = (prompt: string) => {
    if (streaming) return
    const userMsg: ChatMessage = { id: `msg_${Date.now()}`, role: 'user', content: prompt, timestamp: new Date() }
    setMessages((prev) => [...prev, userMsg])
    streamChat(prompt, 'enterprise')
  }

  const handleRetry = async (messageId: string) => {
    if (streaming) return
    const idx = messages.findIndex(m => m.id === messageId)
    if (idx < 1) return
    const userMsg = messages[idx - 1]
    if (userMsg.role !== 'user') return
    let restored: FileAttachment[] = []
    try { restored = await hydrateMessageAttachments(userMsg.attachments) } catch {}
    const images = restored.filter(a => a.kind === 'image').map(a => ({ name: a.name, data: a.data, mimeType: a.type || 'image/jpeg' }))
    setMessages(prev => prev.slice(0, idx - 1))
    const newUser: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: userMsg.content,
      timestamp: new Date(),
      attachments: userMsg.attachments,
      ...(images.length > 0 ? { images } : {}),
    }
    setMessages(prev => [...prev, newUser])
    streamChat(userMsg.content, undefined, undefined, images.length > 0 ? images : undefined)
  }

  const handleRetryWithContext = async (messageId: string) => {
    if (streaming) return
    const idx = messages.findIndex(m => m.id === messageId)
    if (idx < 1) return
    const userMsg = messages[idx - 1]
    const assistantMsg = messages[idx]
    if (userMsg.role !== 'user') return
    const context = [
      `Previous response:\n${assistantMsg.content.slice(0, 2000)}`,
      assistantMsg.toolCalls?.length ? `\nPrevious tool calls: ${assistantMsg.toolCalls.map(t => `${t.name} (${t.status})`).join(', ')}` : '',
      `\n\nPlease retry the original request with this context. Original request: ${userMsg.content}`,
    ].join('')
    let restored: FileAttachment[] = []
    try { restored = await hydrateMessageAttachments(userMsg.attachments) } catch {}
    const images = restored.filter(a => a.kind === 'image').map(a => ({ name: a.name, data: a.data, mimeType: a.type || 'image/jpeg' }))
    const newUser: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: userMsg.content,
      timestamp: new Date(),
      attachments: userMsg.attachments,
      ...(images.length > 0 ? { images } : {}),
    }
    setMessages(prev => [...prev, newUser])
    streamChat(context, undefined, undefined, images.length > 0 ? images : undefined)
  }

  const handleContinue = (messageId: string) => {
    if (streaming) return
    const msg = messages.find(m => m.id === messageId)
    if (!msg?.continuationState) return
    // Clear the continuation prompt from the message it's resuming from, so
    // it doesn't linger once the new assistant turn starts streaming in.
    setMessages(prev => prev.map(m => m.id === messageId ? { ...m, continuationState: undefined } : m))
    streamChat('', chatMode, msg.continuationState.conversation)
  }

  const handleFeedback = (messageId: string, type: 'positive' | 'negative', reason?: string, comment?: string) => {
    setMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, feedback: type, feedbackReason: reason, feedbackComment: comment } : m
    ))
    api.chatFeedback(messageId, type, reason, comment, activeSessionId ?? undefined).catch(() => {})
  }

  const handleApprove = async (requestId: string) => {
    // Fixed a real, previously-undiscovered gap: this used to call
    // approveMutation (api.approve -> POST /dashboard/approve), which is a
    // stale endpoint that updates an unrelated table and was already
    // flagged as no longer called by any working frontend path. The
    // actually-working approval endpoint, confirmed by checking what the
    // bell-icon Pending Approvals popup itself calls, is
    // api.approveAction -> POST /dashboard/approvals/{token}/approve.
    try {
      const res = await api.approveAction(requestId)
      setMessages((prev) => prev.map((m) => {
        if (m.approvalCard?.id !== requestId) return m
        // Auto-continue, per direct instruction: same experience as this
        // very conversation's own approval flow -- approve, then the same
        // reasoning thread continues with the real result, rather than
        // requiring a fresh message that risks the model re-deciding the
        // whole task from scratch. res.continuation is the model's own
        // continuation text after seeing the real tool result -- appended
        // directly to content, same as any other streamed text, so any
        // image link Kimi includes renders exactly like it already does
        // for a normal, non-approval generation.
        const nextCard: ApprovalCard = res.continuation_pending
          ? { id: res.continuation_pending.id, action: res.continuation_pending.action, tier: res.continuation_pending.tier, details: res.continuation_pending.details, status: 'pending' }
          : { ...m.approvalCard, status: 'approved' }
        return {
          ...m,
          approvalCard: nextCard,
          content: res.continuation ? (m.content ? m.content + '\n\n' + res.continuation : res.continuation) : m.content,
        }
      }))
    } catch {
      // leave status as pending on failure -- don't claim success that didn't happen
    }
  }

  const handleRenameChat = async () => {
    if (!activeSessionId) return
    const next = window.prompt('Rename chat', chatTitle)?.trim()
    if (!next || next === chatTitle) { setChatMenuOpen(false); return }
    const res = await fetch(`/api/dashboard/sessions/${activeSessionId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: next }),
    })
    if (res.ok) { setChatTitle(next); bumpSessionList() }
    setChatMenuOpen(false)
  }

  const handleShareChat = async () => {
    const shareUrl = activeSessionId ? `${window.location.origin}/?session=${activeSessionId}` : window.location.origin
    if (navigator.share) {
      try { await navigator.share({ title: chatTitle, url: shareUrl }) } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(shareUrl)
    }
    setChatMenuOpen(false)
  }

  const handleArchiveChat = async () => {
    if (!activeSessionId) return
    const res = await fetch(`/api/dashboard/sessions/${activeSessionId}`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ archived: true }),
    })
    if (res.ok) { bumpSessionList(); setActiveSessionId(null) }
    setChatMenuOpen(false)
  }

  const handleReject = async (requestId: string) => {
    try {
      await api.denyAction(requestId)
      setMessages((prev) => prev.map((m) =>
        m.approvalCard?.id === requestId ? { ...m, approvalCard: { ...m.approvalCard, status: 'denied' as const } } : m
      ))
    } catch {
      // leave status as pending on failure
    }
  }

  const handleUserRetry = async (msg: ChatMessage) => {
    if (streaming) return
    const idx = messages.findIndex(m => m.id === msg.id)
    if (idx === -1) return
    let restored: FileAttachment[] = []
    try { restored = await hydrateMessageAttachments(msg.attachments) } catch {}
    const images = restored.filter(a => a.kind === 'image').map(a => ({ name: a.name, data: a.data, mimeType: a.type || 'image/jpeg' }))
    setMessages(prev => prev.slice(0, idx))
    const newUser: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: msg.content,
      timestamp: new Date(),
      attachments: msg.attachments,
      ...(images.length > 0 ? { images } : {}),
    }
    setMessages(prev => [...prev, newUser])
    streamChat(msg.content, undefined, undefined, images.length > 0 ? images : undefined)
  }

  return (
    <div className="ops-conversation flex h-full w-full overflow-hidden bg-surface">
      <div className="ops-chat-shell relative flex flex-col min-h-0 min-w-0 w-full flex-1 overflow-hidden bg-surface">
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 h-14 shrink-0 w-full">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveSessionId(null)}
              className="ops-chat-back cursor-pointer"
              aria-label="Back to chat history"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <div className="text-sm font-semibold text-text-primary">OPS</div>
              <div className="flex items-center gap-1.5 text-[10px] text-text-muted">
                <span className={cn('h-1.5 w-1.5 rounded-full', streaming ? 'bg-warning animate-pulse' : 'bg-success')} />
                {streaming ? 'Responding' : 'Ready'}
              </div>
            </div>
          </div>
          <div className="relative" ref={chatMenuRef}>
            <button type="button" onClick={() => setChatMenuOpen(!chatMenuOpen)} className="ops-chat-more" aria-label="Chat options">
              <MoreHorizontal className="h-5 w-5" />
            </button>
            {chatMenuOpen && (
              <PortalMenu anchorRef={chatMenuRef} direction="down" onClose={() => setChatMenuOpen(false)}>
                <div className="ops-chat-options w-56 rounded-2xl p-2">
                  <button type="button" onClick={handleRenameChat}><Pencil className="h-4 w-4" /><span>Rename chat</span></button>
                  <button type="button" onClick={handleShareChat}><Share2 className="h-4 w-4" /><span>Share chat</span></button>
                  <button type="button" onClick={handleArchiveChat}><Archive className="h-4 w-4" /><span>Archive chat</span></button>
                  {adminConsoleAllowed && <>
                    <div className="my-1 border-t border-border/60" />
                    <button type="button" role="switch" aria-checked={devConsoleOpen} onClick={() => { setDevConsoleOpen(!devConsoleOpen); setChatMenuOpen(false) }}>
                      <Terminal className="h-4 w-4" /><span className="flex-1 text-left">Administration console</span>
                      <span className={cn('ops-menu-toggle', devConsoleOpen && 'is-on')}><i /></span>
                    </button>
                  </>}
                </div>
              </PortalMenu>
            )}
          </div>
        </div>

        <div
          className={cn('ops-chat-scroll flex-1 px-4 sm:px-6 overflow-y-auto overflow-x-hidden', dragOver && 'ring-2 ring-accent ring-inset rounded-lg')}
          style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
          onDragOver={(e: React.DragEvent) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {dragOver && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-accent/5 rounded-lg pointer-events-none">
              <div className="text-accent text-sm font-medium flex items-center gap-2">
                <Paperclip className="h-5 w-5" /> Drop files here
              </div>
            </div>
          )}
          <div className="pt-6 sm:pt-8 pb-52 sm:pb-48 w-full max-w-3xl mx-auto overflow-hidden">
            {messages.map((msg) => {
              if (msg.role === 'system') {
                return (
                  <div key={msg.id} className="hidden" aria-hidden="true">{msg.content}</div>
                )
              }
              if (msg.role === 'user') {
                return (
                  <UserBubble
                    key={msg.id}
                    message={msg}
                    onRetry={() => { void handleUserRetry(msg) }}
                    onEdit={() => { void handleEdit(msg.id) }}
                  />
                )
              }
              return (
                <div key={msg.id}>
                  <AssistantResponse
                    message={msg}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onRetry={() => handleRetry(msg.id)}
                    onRetryWithContext={() => handleRetryWithContext(msg.id)}
                    onFeedback={(type, reason, comment) => handleFeedback(msg.id, type, reason, comment)}
                    onTogglePreview={handleTogglePreview}
                    previewOpen={previewOpen}
                    previewMessageId={previewContent?.messageId ?? null}
                    isStreaming={streaming && msg.id === messages[messages.length - 1]?.id}
                  />
                  {msg.continuationState && (
                    <div className="flex items-center gap-2 px-1 pb-2">
                      <button
                        type="button"
                        disabled={streaming}
                        onClick={() => handleContinue(msg.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent text-white text-xs font-medium hover:opacity-90 disabled:opacity-50 transition-opacity cursor-pointer"
                      >
                        <RefreshCw className="h-3 w-3" />
                        Continue
                      </button>
                      <span className="text-[10px] text-text-muted">
                        {msg.continuationState.reason === 'cost'
                          ? `stopped at ~${formatGbp(msg.continuationState.costUsd)} of the ${formatGbp(msg.continuationState.costCapUsd)} step limit`
                          : `stopped after ${msg.continuationState.turnsUsed} steps`}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
            <div ref={scrollRef} />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 px-4 py-2 bg-danger/10 border-t border-danger/20 shrink-0">
            <AlertCircle className="h-3 w-3 text-danger" />
            <span className="text-xs text-danger flex-1">{error}</span>
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setError(null)}>
              <RefreshCw className="h-3 w-3" />
            </Button>
          </div>
        )}

        <div className="ops-composer-dock absolute inset-x-0 bottom-0 z-20 px-3 sm:px-6 pt-8 pb-[max(.6rem,env(safe-area-inset-bottom))] space-y-2 w-full">
        {devConsoleOpen && adminConsoleAllowed && (
          <div className="border-t border-border bg-[#1a1a2e] p-3 shrink-0">
            <div className="flex items-center gap-2 mb-2">
              <Terminal className="h-3 w-3 text-green-400" />
              <span className="text-xs text-green-400 font-mono">Administration console</span>
              <Badge variant="danger" className="text-[9px]">Super Admin</Badge>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {DEV_ACTIONS.map(({ label, prompt }) => (
                <Button key={label} variant="outline" size="sm" className="h-6 text-[10px] px-2 border-green-800 text-green-400 hover:bg-green-900/30" disabled={streaming} onClick={() => void handleAdminAction(prompt)}>
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}

          {activeProjectId ? (
            <div className="flex gap-1.5 flex-wrap">
              {IDEA_ACTIONS.map(({ label, icon: Icon, prompt }) => (
                <Button key={label} variant="outline" size="sm" className="h-7 text-xs gap-1 border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10" onClick={() => handleQuickAction(prompt)} disabled={streaming}>
                  <Icon className="h-3 w-3" />
                  {label}
                </Button>
              ))}
            </div>
          ) : (
            <div className="hidden sm:flex gap-1.5 flex-wrap">
              {QUICK_ACTIONS.map(({ label, icon: Icon, prompt }) => (
                <Button key={label} variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => handleQuickAction(prompt)} disabled={streaming}>
                  <Icon className="h-3 w-3" />
                  {label}
                </Button>
              ))}
            </div>
          )}
          {latestArtifact && (
            <button
              onClick={() => handleTogglePreview(latestArtifact)}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs w-full sm:w-auto transition-colors',
                previewOpen && previewContent?.messageId === latestArtifact.messageId
                  ? 'border-accent/40 bg-accent/10 text-accent'
                  : 'border-border bg-surface-elevated text-text-secondary hover:border-accent/30 hover:text-accent',
              )}
            >
              <Code2 className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{latestArtifact.title || `${latestArtifact.type.toUpperCase()} preview`}</span>
              <span className="ml-auto text-[10px] text-text-muted shrink-0">
                {previewOpen && previewContent?.messageId === latestArtifact.messageId ? 'Close' : 'View'}
              </span>
            </button>
          )}
          <AttachmentChips files={attachments} onRemove={(idx) => setAttachments(prev => prev.filter((_, i) => i !== idx))} />
          <div className="ops-glass-composer max-w-3xl mx-auto rounded-[1.7rem] transition-all">
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} className="hidden" />
            <input ref={photoInputRef} type="file" accept="image/*" multiple onChange={handleFileSelect} className="hidden" />
            <input ref={filesInputRef} type="file" accept=".pdf,.txt,.md,.js,.ts,.json,.csv,.docx,.xlsx,.mp4,.webm,video/mp4,video/webm" multiple onChange={handleFileSelect} className="hidden" />
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value)
                e.target.style.height = 'auto'
                e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px'
              }}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault()
                  void handleSend()
                }
              }}
              placeholder={streaming ? 'OPS is responding...' : 'Message OPS'}
              disabled={streaming}
              rows={1}
              className="w-full min-h-[48px] max-h-40 bg-transparent px-4 pt-3.5 pb-1 text-[15px] text-text-primary placeholder:text-text-muted focus:outline-none disabled:opacity-50 resize-none overflow-y-auto leading-5"
              style={{ height: '44px' }}
            />
            <div className="flex items-center gap-1.5 px-3 pb-2.5 pt-0.5">
              <div className="relative" ref={attachMenuRef}>
                <button
                  onClick={() => setAttachMenuOpen(!attachMenuOpen)}
                  disabled={streaming || attachments.length >= MAX_FILES}
                  className="h-7 w-7 rounded-full bg-surface-elevated hover:bg-border flex items-center justify-center text-text-muted hover:text-text-primary transition-colors disabled:opacity-40 shrink-0"
                  title="Add files, images, photos or videos"
                >
                  <Plus className="h-4 w-4" />
                </button>
                {attachMenuOpen && (
                  <>
                    <div className="fixed inset-0 bg-black/30 z-[9998] sm:hidden" onClick={() => setAttachMenuOpen(false)} />
                    <div className={cn(
                      'z-[9999] bg-surface border border-border shadow-2xl p-2',
                      'fixed inset-x-0 bottom-0 rounded-t-2xl px-4 pb-6 pt-3',
                      'sm:absolute sm:inset-auto sm:bottom-full sm:left-0 sm:mb-2 sm:rounded-xl sm:w-auto sm:p-2'
                    )}>
                      <div className="flex justify-center pt-1 pb-2 sm:hidden">
                        <div className="w-8 h-1 rounded-full bg-border" />
                      </div>
                      <div className="flex gap-2 justify-center">
                        <button onClick={() => { cameraInputRef.current?.click(); setAttachMenuOpen(false) }} className="flex flex-col items-center justify-center gap-1.5 w-[72px] h-[72px] rounded-xl bg-surface-elevated hover:bg-border text-text-secondary transition-colors">
                          <Camera className="h-5 w-5" />
                          <span className="text-[10px] font-medium">Camera</span>
                        </button>
                        <button onClick={() => { photoInputRef.current?.click(); setAttachMenuOpen(false) }} className="flex flex-col items-center justify-center gap-1.5 w-[72px] h-[72px] rounded-xl bg-surface-elevated hover:bg-border text-text-secondary transition-colors">
                          <Image className="h-5 w-5" />
                          <span className="text-[10px] font-medium">Photos</span>
                        </button>
                        <button onClick={() => { filesInputRef.current?.click(); setAttachMenuOpen(false) }} className="flex flex-col items-center justify-center gap-1.5 w-[72px] h-[72px] rounded-xl bg-surface-elevated hover:bg-border text-text-secondary transition-colors">
                          <FileText className="h-5 w-5" />
                          <span className="text-[10px] font-medium">Files</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="relative" ref={modelMenuRef}>
                <button
                  onClick={() => setModelMenuOpen(!modelMenuOpen)}
                  disabled={streaming}
                  className="h-8 px-2.5 rounded-full hover:bg-border flex items-center gap-1.5 text-[11px] text-text-secondary transition-colors disabled:opacity-40"
                >
                  {(() => {
                    const active = MODEL_GROUPS.flatMap(g => g.models).find(m => m.mode === chatMode)
                    const prov = active?.provider
                    const Icon = prov === 'OPS' ? Brain : prov === 'Anthropic' ? Crown : prov === 'OpenAI' ? Sparkles : prov === 'Gemini' ? Zap : Layers
                    return <Icon className="h-3 w-3 shrink-0" />
                  })()}
                  <span className="max-w-[6rem] truncate">
                    {modeLabels[chatMode] || MODEL_GROUPS.flatMap(g => g.models).find(m => m.mode === chatMode)?.label || 'Select model'}
                  </span>
                  {(() => {
                    if (chatMode !== 'ops-mini' && chatMode !== 'ops-1') return null
                    const configured = chatMode === 'ops-mini' ? fastGeneralModel : toolOrchestratorModel
                    const live = opsActualModel || configured
                    if (!live) return null
                    return <span className="text-[9px] text-accent/70 truncate max-w-[5rem]">{live.split('/').pop()}</span>
                  })()}
                  <ChevronDown className="h-2.5 w-2.5 text-text-muted" />
                </button>
                {modelMenuOpen && (
                  <>
                    <div className="fixed inset-0 bg-black/30 z-[9998] sm:hidden" onClick={() => setModelMenuOpen(false)} />
                    <div className={cn(
                      'z-[9999] bg-surface border border-border shadow-2xl overflow-y-auto',
                      'fixed inset-x-0 bottom-0 rounded-t-2xl max-h-[70vh]',
                      'sm:absolute sm:inset-auto sm:bottom-full sm:left-0 sm:mb-2 sm:rounded-xl sm:w-72 sm:max-h-[50vh]'
                    )}>
                      <div className="flex justify-center pt-2 pb-1 sm:hidden">
                        <div className="w-8 h-1 rounded-full bg-border" />
                      </div>
                      {MODEL_GROUPS.map(group => (
                        <div key={group.group}>
                          <div className="px-3 pt-3 pb-1">
                            <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">{group.group}</div>
                            <div className="text-[10px] text-text-muted/60">{group.desc}</div>
                          </div>
                          {group.models.map(model => {
                            const Icon = model.provider === 'OPS' ? Brain : model.provider === 'Anthropic' ? Crown : model.provider === 'OpenAI' ? Sparkles : model.provider === 'Gemini' ? Zap : Layers
                            return (
                              <button
                                key={model.mode}
                                onClick={() => { setChatMode(model.mode); setModelMenuOpen(false) }}
                                className={cn(
                                  'flex items-center gap-3 w-full px-3 py-2 text-left text-xs transition-colors',
                                  chatMode === model.mode ? 'bg-accent/10 text-accent' : 'text-text-secondary hover:bg-surface-elevated'
                                )}
                              >
                                <Icon className="h-3.5 w-3.5 shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium flex items-center gap-1.5">
                                    {modeLabels[model.mode] || model.label}
                                    {model.mode === DEFAULT_MODE && (
                                      <span className="text-[9px] px-1 py-px rounded bg-accent/15 text-accent font-semibold leading-none">default</span>
                                    )}
                                  </div>
                                  {model.mode === 'ops-1' ? (
                                    <div className="mt-0.5 space-y-0.5 text-[10px] text-text-muted">
                                      <div className="flex flex-wrap items-center gap-x-1.5">
                                        <span className="font-medium text-text-secondary">{roleLabel('tool_orchestrator')}:</span>
                                        <span>{compactModelName(toolOrchestratorModel) || 'unavailable'}</span>
                                        {toolOrchestratorRole?.active?.qualification_state === 'qualified' && (
                                          <span className="rounded bg-emerald-500/10 px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-emerald-500">qualified</span>
                                        )}
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-1.5">
                                        <span className="font-medium text-text-secondary">{roleLabel('deep_reasoner')}:</span>
                                        <span>{compactModelName(deepReasonerModel) || 'unavailable'}</span>
                                        {deepReasonerRole?.active?.qualification_state === 'qualified' && (
                                          <span className="rounded bg-violet-500/10 px-1 py-px text-[8px] font-semibold uppercase tracking-wide text-violet-500">on demand</span>
                                        )}
                                      </div>
                                      {verifierModel && (
                                        <div>
                                          <span className="font-medium text-text-secondary">{roleLabel('verifier')}:</span>{' '}
                                          {compactModelName(verifierModel)}
                                        </div>
                                      )}
                                      {opsFallbackModels.length > 0 && (
                                        <div>
                                          <span className="font-medium text-text-secondary">Fallback:</span>{' '}
                                          {opsFallbackModels.map(compactModelName).filter(Boolean).join(', ')}
                                        </div>
                                      )}
                                    </div>
                                  ) : model.mode === 'ops-mini' ? (
                                    <div className="mt-0.5 space-y-0.5 text-[10px] text-text-muted">
                                      <div>
                                        <span className="font-medium text-text-secondary">{roleLabel('fast_general')}:</span>{' '}
                                        {compactModelName(fastGeneralModel) || 'unavailable'}
                                      </div>
                                      {visionModel && (
                                        <div>
                                          <span className="font-medium text-text-secondary">{roleLabel('vision')}:</span>{' '}
                                          {compactModelName(visionModel)}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-[10px] text-text-muted">{model.desc}</div>
                                  )}
                                </div>
                                {chatMode === model.mode && <Check className="h-3 w-3 shrink-0" />}
                              </button>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <div className="flex-1" />
              <button
                onClick={() => setError('Voice mode coming soon (Q44)')}
                disabled={streaming}
                className="h-7 w-7 rounded-full bg-surface-elevated hover:bg-border flex items-center justify-center text-text-muted hover:text-text-primary transition-colors disabled:opacity-40 shrink-0"
                title="Voice mode"
              >
                <Mic className="h-3.5 w-3.5" />
              </button>
              {streaming ? (
                <button
                  onClick={handleStop}
                  className="h-8 w-8 rounded-full flex items-center justify-center transition-all shrink-0 bg-red-500 hover:bg-red-600"
                  title="Stop"
                >
                  <Square className="h-3 w-3 text-white fill-white" />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={
                    (!input.trim() && attachments.length === 0) ||
                    attachments.some(a => a.processing || !!a.error)
                  }
                  className="h-8 w-8 rounded-full flex items-center justify-center transition-all shrink-0 disabled:opacity-40"
                  style={{
                    background: (
                      (!input.trim() && attachments.length === 0) ||
                      attachments.some(a => a.processing || !!a.error)
                    ) ? 'var(--color-border)' : 'var(--color-accent)'
                  }}
                  title={attachments.some(a => a.processing) ? 'Waiting for attachments' : attachments.some(a => a.error) ? 'Remove failed attachments' : 'Send'}
                >
                  <ArrowUp className="h-3.5 w-3.5 text-white" />
                </button>
              )}
            </div>
          </div>
          <p className="max-w-3xl mx-auto text-center text-[10px] text-text-muted px-2">
            OPS can make mistakes. Check important information.
          </p>
        </div>
      </div>

      {previewOpen && previewContent && (
        <ArtifactCanvas
          artifact={previewContent}
          onClose={() => { setPreviewOpen(false); setPreviewContent(null) }}
        />
      )}
    </div>
  )
}
